import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import ZenPagination from "../components/ZenPagination";
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, TrendingDown, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, CreditCard, ClipboardList, RefreshCw, Save,
  Home, ChevronsLeft, ChevronsRight, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon,
  MoreHorizontal, Minus,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { StickyScrollWrapper } from "../components/StickyScrollWrapper";
import { AuthCtx, UICtx, StockSettingsCtx } from "../appContexts";
import { fmt, fmtDate, fmtDT, ono, MONEY, convertPrice } from "../appUtils";
import { translations, CURRENCY_SYMBOL, STATUS_LABELS } from "../mock";
import { CardPayModal } from "../cardPayment";
import { BarcodeScannerModal, BarcodeResultDialog } from "../barcodeScanner";
import { ItemLabelModal } from "../itemLabels";
import { makeTr } from "../i18n";
import { ModuleIcon, PName, MODULE_GRADIENTS } from "../components/layout/Sidebar";
import PrintTemplateMenu from "../components/PrintTemplateMenu";
import { DEFAULT_EMAIL_TEMPLATES, renderEmailTemplate, fetchEmailTemplate, logSend, apiErrorMessage, printViaIframe } from "../utils/pdfUtils";
import { ZEN_PDF_FONT_FACE } from "../quotes";
import { PRICE_RULE_PRODUCT_TYPES, PRICE_RULE_FABRIC_GROUPS, nattyPliseFabricGroupKey } from "../utils/quoteUtils";
import logo from "../assets/zen-logo.png";

// ── PDF yardımcı fonksiyonlar ─────────────────────────────────────────────────
export async function loadLogoDataUrl() {
  try {
    const res = await fetch(logo);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

export async function fetchRates() {
  try {
    const { data } = await api.get("/rates");
    return { eurToTl: data.eurToTl || 38, usdToTl: data.usdToTl || 34, tcmbDate: data.tcmbDate };
  } catch (e) { return { eurToTl: 38, usdToTl: 34, tcmbDate: "-" }; }
}

function convAllCurrencies(amount, fromCur, rates) {
  const eurTl = rates.eurToTl, usdTl = rates.usdToTl;
  let tl = Number(amount) || 0;
  if (fromCur === "EUR") tl = tl * eurTl;
  else if (fromCur === "USD") tl = tl * usdTl;
  return { tl, eur: tl / eurTl, usd: tl / usdTl };
}

function fmtPrint(n, cur = "TL") {
  const sym = { TL: "₺", EUR: "€", USD: "$" }[cur] || "";
  return `${sym}${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function whatsappShare(phone, message) {
  const clean = (phone || "").replace(/[^\d]/g, "");
  const url = `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank");
}

export function computeStatementMovements(customer, orders, payments, invoices) {
  const movements = [];
  const orderById = Object.fromEntries((orders || []).map(o => [o.id, o]));
  orders.filter(o => o.customerId === customer.id && o.status !== "teklif" && o.status !== "iptal").forEach(o => {
    movements.push({
      date: (o.approvedAt || o.createdAt || "").slice(0, 10),   // ISO — sıralama + filtre için
      type: "Sipariş", ref: ono(o),
      note: `${(o.items || [])[0]?.product || ""}${o.items?.length > 1 ? ` +${o.items.length - 1} kalem` : ""}`,
      debit: o.total, credit: 0, currency: o.currency,
    });
  });
  (invoices || []).filter(inv => inv.customerId === customer.id).forEach(inv => {
    const isDebit = inv.type === "satis" || inv.type === "alis";
    const hasLinkedOrder = !!inv.orderId;
    // Siparişe bağlı faturada sadece KDV tutarı gösterilir
    const displayAmount = hasLinkedOrder ? (inv.vatAmount || 0) : (inv.total || 0);
    const linkedOrder = hasLinkedOrder ? orderById[inv.orderId] : null;
    const typeLbl = hasLinkedOrder
      ? "KDV Faturası"
      : ({ satis: "Satış Fat.", alis: "Alış Fat.", satis_iade: "Satış İade", alis_iade: "Alış İade", irsaliye: "İrsaliye" }[inv.type] || "Fatura");
    const noteText = hasLinkedOrder
      ? `${linkedOrder ? ono(linkedOrder) : inv.orderId} sipariş KDV'si`
      : `${(inv.items || []).length} kalem`;
    movements.push({
      date: (inv.issueDate || "").slice(0, 10), type: typeLbl, ref: inv.docNo || inv.id,
      note: noteText,
      debit: isDebit ? displayAmount : 0, credit: !isDebit ? displayAmount : 0, currency: inv.currency,
    });
  });
  payments.filter(p => p.customerId === customer.id).forEach(p => {
    const kind = p.kind || "tahsilat";
    const kindLbl = { tahsilat: "Tahsilat", tediye: "Tediye", alacak_tahakkuku: "Alacak Th.", borc_tahakkuku: "Borç Th." }[kind];
    const methodLbl = { havale: "Havale", nakit: "Nakit", banka: "Banka", kart: "Kredi Kartı", cek: "Çek" }[p.method] || p.method;
    const isCredit = ["tahsilat", "borc_tahakkuku"].includes(kind);
    movements.push({
      date: (p.date || "").slice(0, 10), type: kindLbl, ref: p.id,
      note: `${methodLbl}${p.checkNo ? ` · Çek: ${p.checkNo}` : ""}${p.note ? ` · ${p.note}` : ""}`,
      debit: !isCredit ? p.amount : 0, credit: isCredit ? p.amount : 0, currency: p.currency,
    });
  });
  movements.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return movements;
}

export function buildStatementHTML(customer, movements, opts, rates, logoUrl) {
  const cur = customer.currency || "TL";
  const filtered = movements.filter(m => (!opts.from || m.date >= opts.from) && (!opts.to || m.date <= opts.to));
  let running = customer.openingBalance || 0;
  const rows = filtered.map(m => {
    running += (m.debit || 0) - (m.credit || 0);
    return `<tr>
      <td>${(m.date ? fmtDate(m.date) : "-")}</td>
      <td>${m.type}</td>
      <td class="b">${(m.ref || "").replace(/</g, "&lt;")}</td>
      <td>${(m.note || "").replace(/</g, "&lt;")}</td>
      <td class="r">${m.debit ? fmtPrint(m.debit, m.currency) : ""}</td>
      <td class="r">${m.credit ? fmtPrint(m.credit, m.currency) : ""}</td>
      <td class="r b">${fmtPrint(running, cur)}</td>
    </tr>`;
  }).join("");

  const totalBalance = customer.balance || 0;
  const closingConv = convAllCurrencies(totalBalance, cur, rates);
  const html = `
<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>ZEN Cari Ekstre - ${customer.name}</title>
<style>
${ZEN_PDF_FONT_FACE}
  @page { size: A4 portrait; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: ZenSans, 'Segoe UI', 'Roboto', Arial, sans-serif; color: #1a1a1a; margin: 0; font-size: 11.5px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #E5322A; }
  .header .logo img { height: 46px; object-fit: contain; }
  .title { text-align: center; margin: 16px 0 6px; }
  .title h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: 3px; color: #E5322A; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin: 10px 0 16px; padding: 10px 14px; background: #F5F5F7; border: 1px solid #E5E5EA; border-radius: 6px; }
  .meta b { color: #333; margin-right: 8px; }
  table.plist { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  table.plist th { background: #E5322A; color: #fff; text-align: left; padding: 6px 6px; border: 1px solid #B0221D; font-weight: 700; font-size: 10.5px; }
  table.plist td { border: 1px solid #C8C8CE; padding: 6px 6px; vertical-align: middle; }
  table.plist tr:nth-child(even) td { background: #FAFAFB; }
  td.r { text-align: right; } td.b { font-weight: 700; }
  .closing { margin-top: 14px; padding: 12px 16px; background: ${totalBalance > 0 ? "#FEF2F2" : "#F0FDF4"}; border-left: 5px solid ${totalBalance > 0 ? "#DC2626" : "#16A34A"}; border-radius: 4px; display: flex; justify-content: space-between; align-items: center; font-size: 15px; font-weight: 800; color: ${totalBalance > 0 ? "#991B1B" : "#166534"}; }
  .conv-box { margin-top: 12px; padding: 12px 14px; background: #FFF7ED; border: 1px dashed #FB923C; border-radius: 6px; font-size: 11px; }
  .conv-box .head { font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #9A3412; font-weight: 700; margin-bottom: 6px; }
  .conv-box .row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .conv-box .row div { display: flex; justify-content: space-between; padding: 6px 8px; background: #fff; border-radius: 4px; border: 1px solid #FED7AA; }
  .conv-box .row div b { color: #9A3412; }
  .footer { position: fixed; bottom: 12mm; left: 12mm; right: 12mm; font-size: 9px; color: #888; padding-top: 6px; border-top: 1px solid #E5E5EA; text-align: center; }
</style></head>
<body>
  <div class="header">
    <div class="logo">${logoUrl ? `<img src="${logoUrl}" alt="ZEN">` : `<h2 style="margin:0;color:#E5322A">ZEN</h2>`}</div>
    <div style="text-align:right;font-size:10.5px;line-height:1.5;color:#444">
      <b>ZEN Mekanizmalı Perde Sistemleri</b><br>
      info@zenperde.com · www.zenperde.com<br>
      TCMB Kur: 1 EUR = ₺${rates.eurToTl.toFixed(2)} · 1 USD = ₺${rates.usdToTl.toFixed(2)}
    </div>
  </div>
  <div class="title"><h1>CARİ EKSTRE</h1></div>
  <div class="meta">
    <div><b>Cari Adı:</b> ${(customer.name || "-").replace(/</g, "&lt;")}</div>
    <div><b>Cari Tipi:</b> ${(customer.type || "Yurt İçi Müşteri").toUpperCase()}</div>
    <div><b>Adres:</b> ${(customer.address || "-").replace(/</g, "&lt;")}</div>
    <div><b>Tarih Aralığı:</b> ${opts.from || "Başlangıçtan"} — ${opts.to || "Bugüne"}</div>
    <div><b>Telefon:</b> ${(customer.phone || "-").replace(/</g, "&lt;")}</div>
    <div><b>Ekstre Tarihi:</b> ${new Date().toLocaleDateString("tr-TR")}</div>
  </div>
  <table class="plist">
    <thead><tr>
      <th>Tarih</th><th>Tür</th><th>Referans</th><th>Açıklama</th>
      <th style="text-align:right">Borç</th><th style="text-align:right">Alacak</th><th style="text-align:right">Bakiye</th>
    </tr></thead>
    <tbody>${rows || `<tr><td colspan="7" style="text-align:center;color:#999;padding:20px">Bu dönemde hareket yok.</td></tr>`}</tbody>
  </table>
  <div class="closing">
    <span>TOPLAM BAKİYE<div style="font-size:10px;font-weight:400;color:${totalBalance > 0 ? "#DC2626" : "#16A34A"};opacity:0.8;margin-top:2px">${totalBalance > 0 ? "Müşteri borçlu" : "Müşteri alacaklı"}</div></span>
    <span>${fmtPrint(totalBalance, cur)} ${cur}</span>
  </div>
  <div class="conv-box">
    <div class="head">Diğer Para Birimlerinde Karşılık</div>
    <div class="row">
      <div><span>Türk Lirası</span><b>${fmtPrint(closingConv.tl, "TL")}</b></div>
      <div><span>Euro</span><b>${fmtPrint(closingConv.eur, "EUR")}</b></div>
      <div><span>ABD Doları</span><b>${fmtPrint(closingConv.usd, "USD")}</b></div>
    </div>
    <div style="margin-top:6px;font-size:9.5px;color:#9A3412">TCMB kuru referans (${rates.tcmbDate || new Date().toLocaleDateString("tr-TR")}).</div>
  </div>
  <div class="footer">ZEN Mekanizmalı Perde Sistemleri · www.zenperde.com · info@zenperde.com</div>
</body></html>`;
  return { html, closingBalance: totalBalance };
}

export async function generateStatementPDF(customer, orders, payments, opts = { from: null, to: null, invoices: [] }) {
  const logoUrl = await loadLogoDataUrl();
  const rates = await fetchRates();
  const movements = computeStatementMovements(customer, orders, payments, opts.invoices || []);
  const { html, closingBalance } = buildStatementHTML(customer, movements, opts, rates, logoUrl);
  printViaIframe(html, false);
  return closingBalance;
}

// Ekstreyi gerçek bir PDF'e çevirip paylaşılabilir link üretir + WhatsApp'ta açar
async function shareStatementViaWhatsApp(customer, orders, payments, invoices, opts = {}) {
  if (!customer.phone) { toast.error("Müşterinin telefon numarası tanımlı değil"); return; }
  const logoUrl = await loadLogoDataUrl();
  const rates = await fetchRates();
  const movements = computeStatementMovements(customer, orders, payments, invoices);
  const { html, closingBalance } = buildStatementHTML(customer, movements, opts, rates, logoUrl);
  const fname = `ZEN-Ekstre-${customer.name.replace(/\s+/g, "_")}.pdf`;
  const { data } = await api.post("/share/html-pdf", { html, filename: fname, refType: "statement", refId: customer.id });
  const link = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
  const msg = `Sayın ${customer.name}, güncel cari hesap ekstrenizi hazırladık.\nGüncel bakiye: ${fmtPrint(closingBalance, customer.currency)} ${customer.currency}\nEkstre PDF: ${link}\n\nZEN Mekanizmalı Perde Sistemleri`;
  whatsappShare(customer.phone, msg);
  logSend({ refType: "statement", refId: customer.id, refLabel: customer.name, channel: "whatsapp", to: customer.phone, subject: "Cari Ekstre" });
  return { link, closingBalance };
}

// Ekstreyi şablon + değişkenlerle e-postaya hazırlar ve gönderir
async function sendStatementEmail(customer, orders, payments, invoices, opts = {}) {
  if (!customer.email) throw new Error("Müşterinin e-posta adresi tanımlı değil");
  const logoUrl = await loadLogoDataUrl();
  const rates = await fetchRates();
  const movements = computeStatementMovements(customer, orders, payments, invoices);
  const { html, closingBalance } = buildStatementHTML(customer, movements, opts, rates, logoUrl);
  const tpl = await fetchEmailTemplate("statement_email");
  const { subject, body } = renderEmailTemplate(tpl, {
    musteri_adi: customer.name, bakiye: fmtPrint(closingBalance, customer.currency),
    para_birimi: customer.currency, firma_adi: "ZEN Mekanizmalı Perde Sistemleri",
  });
  await api.post("/email-settings/send-pdf", {
    to: customer.email, subject, html, bodyText: body,
    filename: `ZEN-Ekstre-${customer.name.replace(/\s+/g, "_")}.pdf`,
    refType: "statement", refId: customer.id, refLabel: customer.name,
  });
}

// Hızlı Paylaşım — cari listesinden tek tık ile ekstre gönderimi (WhatsApp / E-posta)
function StatementShareModal({ customer, onClose }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [sending, setSending] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleWA = async () => {
    setSending(true);
    try {
      await shareStatementViaWhatsApp(customer, (await api.get("/orders")).data, (await api.get("/payments")).data, (await api.get("/invoices")).data, { from: from || null, to: to || null });
      toast.success("WhatsApp açıldı — PDF ekstre linki eklendi");
      onClose();
    } catch (e) { toast.error("Ekstre PDF'i hazırlanamadı"); }
    finally { setSending(false); }
  };
  const handleEmail = async () => {
    setSendingEmail(true);
    try {
      await sendStatementEmail(customer, (await api.get("/orders")).data, (await api.get("/payments")).data, (await api.get("/invoices")).data, { from: from || null, to: to || null });
      toast.success(`E-posta gönderildi — ${customer.email}`);
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "E-posta gönderilemedi"));
    } finally { setSendingEmail(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-md p-5 zen-anim-up" onClick={(e) => e.stopPropagation()} data-testid="ekstre-share-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold flex items-center gap-2"><Send className="w-4 h-4 text-[#E5322A]" /> Ekstre Gönder — {customer.name}</h3>
          <button onClick={onClose} data-testid="ekstre-share-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Başlangıç</label><input type="date" className="zen-input !py-2 w-full" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ekstre-share-from" /></div>
          <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Bitiş</label><input type="date" className="zen-input !py-2 w-full" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ekstre-share-to" /></div>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">Başlangıç boş bırakılırsa tüm geçmiş dahil edilir. Bakiye {customer.currency} üzerinden hesaplanır.</p>
        <div className="space-y-2">
          <button onClick={handleWA} disabled={sending} data-testid="ekstre-share-wa-btn" className="w-full zen-btn-ghost !text-emerald-600 border border-emerald-500/30 justify-center">
            <Send className="w-4 h-4" /> {sending ? "Hazırlanıyor..." : "WhatsApp ile Gönder"}
          </button>
          <button onClick={handleEmail} disabled={sendingEmail} data-testid="ekstre-share-email-btn" className="w-full zen-btn-ghost !text-sky-600 border border-sky-500/30 justify-center">
            <Mail className="w-4 h-4" /> {sendingEmail ? "Gönderiliyor..." : "E-posta ile Gönder"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomersModule({ subTab }) {
  const { tr } = useContext(UICtx);
  const { user } = useContext(AuthCtx);
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [statementCust, setStatementCust] = useState(null);
  const [shareCust, setShareCust] = useState(null);
  const [cardPay, setCardPay] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [showImport, setShowImport] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [q, catFilter]);
  const load = async () => setRows((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  // Sidebar subTab → kategori filtresi
  useEffect(() => {
    if (subTab === "tedarikci") setCatFilter("Tedarikçi");
    else if (subTab === "calisan") setCatFilter("Çalışan");
    else setCatFilter("all");
  }, [subTab]);

  const save = async (data) => {
    if (editing?.id) await api.put(`/customers/${editing.id}`, data);
    else await api.post("/customers", data);
    toast.success("Kaydedildi"); setEditing(null); load();
  };
  const del = async (c) => {
    if (!window.confirm(`${c.name} silinsin mi?`)) return;
    await api.delete(`/customers/${c.id}`); toast.success("Silindi"); load();
  };
  const shareWA = (c) => {
    if (!c.phone) { toast.error("Telefon numarası tanımlı değil"); return; }
    const msg = `Merhaba ${c.name.split(" ")[0] || ""},\nZEN Mekanizmalı Perde Sistemleri olarak hizmetinizdeyiz. Şu anki bakiyeniz: ${fmt(c.balance || 0, c.currency)}. Bilgilerinize.`;
    whatsappShare(c.phone, msg);
  };
  const filtered = rows.filter((r) => {
    if (catFilter !== "all" && r.type !== catFilter) return false;
    return (r.name + r.phone + r.taxNo + r.address).toLowerCase().includes(q.toLowerCase());
  });
  const totals = filtered.reduce((acc, c) => {
    const cur = c.currency || "TL";
    acc[cur] = (acc[cur] || 0) + (c.balance || 0);
    return acc;
  }, {});
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Cari sayısı tipe göre (Yurt İçi/Yurt Dışı Müşteri, Tedarikçi, Çalışan)
  const catCounts = rows.reduce((acc, r) => {
    acc[r.type] = (acc[r.type] || 0) + 1;
    return acc;
  }, {});
  const CATEGORIES = [
    { id: "all", slug: "all", label: "Tümü", color: "slate", count: rows.length },
    { id: "Yurt İçi Müşteri", slug: "yurt-ici", label: "Yurt İçi Müşteri", color: "sky", count: catCounts["Yurt İçi Müşteri"] || 0 },
    { id: "Yurt Dışı Müşteri", slug: "yurt-disi", label: "Yurt Dışı Müşteri", color: "emerald", count: catCounts["Yurt Dışı Müşteri"] || 0 },
    { id: "Tedarikçi", slug: "tedarikci", label: "Tedarikçi", color: "purple", count: catCounts["Tedarikçi"] || 0 },
    { id: "Çalışan", slug: "calisan", label: "Çalışanlar", color: "amber", count: catCounts["Çalışan"] || 0 },
  ];

  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="customers" icon={Users} />{tr("Cari Hesaplar / Müşteriler")}</h2>
          <p className="text-xs text-slate-500 mt-1">{tr("Ekstre, WhatsApp paylaşım ve döviz bazlı bakiye takibi.")}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="new-customer-btn" onClick={() => setEditing({})} className="zen-btn-primary"><Plus className="w-4 h-4" />{tr("Yeni Cari")}</button>
          <button data-testid="bulk-import-btn" onClick={() => setShowImport(true)} className="zen-btn-ghost border border-slate-200 dark:border-[#22304A]"><Upload className="w-4 h-4" />{tr("Toplu İçe Aktar")}</button>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map(c => {
          const active = catFilter === c.id;
          return (
            <button
              key={c.id}
              data-testid={`cari-cat-filter-${c.slug}`}
              onClick={() => setCatFilter(c.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-2 transition-all ${
                active
                  ? `bg-${c.color}-500 text-white shadow-md`
                  : `border border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:border-${c.color}-400`
              }`}
            >
              {tr(c.label)}
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-white/25" : `bg-${c.color}-500/15 text-${c.color}-600 dark:text-${c.color}-400`}`}>{c.count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        {Object.entries(totals).map(([cur, val]) => (
          <div key={cur} className="zen-panel px-4 py-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500">{tr("Toplam Bakiye")} ({cur})</div>
            <div className={`zen-mono text-lg font-bold ${val > 0 ? "text-rose-500" : val < 0 ? "text-emerald-500" : "text-slate-500"}`}>{fmt(val, cur)}</div>
          </div>
        ))}
      </div>

      {/* Geniş arama kutusu — tablo başlamadan hemen önce */}
      <div className="flex items-center gap-3 zen-panel px-4 py-2.5">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          data-testid="customer-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tr("Müşteri ara... (isim, kod, telefon, şehir)")}
          className="bg-transparent outline-none text-sm w-full"
        />
        {q && (
          <button onClick={() => setQ("")} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="zen-panel overflow-hidden">
        <StickyScrollWrapper>
        <table className="zen-table">
          <thead><tr>
            <th style={{width:"36px"}}></th>
            <th>{tr("Sıra")}</th><th>{tr("Cari Adı")}</th><th>{tr("Cari Kodu")}</th>
            <th>{tr("Cari Grubu")}</th><th>{tr("Şehir")}</th><th>{tr("Ülke")}</th>
            <th>{tr("Para Birimi")}</th>
            <th className="text-right">{tr("Fatura B.")}</th>
            <th className="text-right">{tr("Toplam Bakiye")}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {paginated.map((c, idx) => (
              <tr
                key={c.id}
                data-testid={`cari-row-${c.id}`}
                onClick={() => setStatementCust(c)}
                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-[#131B2E] transition-colors"
                title={tr("Detay / Cari Ekstre için tıklayın")}
              >
                <td style={{padding:0, width:"6px"}}>
                  <div className={`w-1.5 h-full absolute left-0 top-0 bottom-0 ${c.type === "Tedarikçi" ? "bg-purple-500" : c.type === "Çalışan" ? "bg-amber-500" : c.type === "Yurt Dışı Müşteri" ? "bg-emerald-500" : "bg-sky-500"}`} />
                </td>
                <td className="text-slate-500 text-xs">#{(page - 1) * pageSize + idx + 1}</td>
                <td className="font-semibold uppercase">{c.name}<div className="text-[10px] text-slate-500 normal-case">{c.email || c.phone}</div></td>
                <td className="zen-mono text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{c.id}</td>
                <td>
                  <span className={`zen-chip whitespace-nowrap ${c.type === "Tedarikçi" ? "bg-purple-500/10 text-purple-500" : c.type === "Çalışan" ? "bg-amber-500/10 text-amber-500" : c.type === "Yurt Dışı Müşteri" ? "bg-emerald-500/10 text-emerald-500" : "bg-sky-500/10 text-sky-500"}`}>
                    {tr(c.type || "").toUpperCase()} ({c.currency})
                  </span>
                  {c.discountPercent > 0 && <div className="text-[10px] text-slate-500 mt-1">%{c.discountPercent} iskonto</div>}
                </td>
                <td className="text-xs uppercase">{c.address?.split(/[\/,]/)[0] || "-"}</td>
                <td className="text-xs uppercase">{c.address?.includes("/") ? c.address.split("/").pop().trim() : "TÜRKİYE"}</td>
                <td><span className="zen-chip bg-[#E5322A]/10 text-[#E5322A]">{c.currency}</span></td>
                <td className="text-right zen-mono font-semibold whitespace-nowrap">{fmt(c.invoiceBalance || 0, c.currency)}</td>
                <td className={`text-right zen-mono font-bold whitespace-nowrap ${c.balance < 0 ? "text-emerald-500" : c.balance > 0 ? "text-rose-500" : ""}`}>
                  <span className="inline-block px-2 py-0.5 rounded" style={{backgroundColor: c.balance > 0 ? "rgba(244, 63, 94, 0.12)" : "transparent"}}>{fmt(c.balance, c.currency)}</span>
                </td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`cari-detail-${c.id}`} onClick={(e) => { e.stopPropagation(); setStatementCust(c); }} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded" title={tr("Cari Ekstre")}><Receipt className="w-4 h-4" /></button>
                    <button data-testid={`ekstre-gonder-${c.id}`} onClick={(e) => { e.stopPropagation(); setShareCust(c); }} className="p-1.5 hover:bg-[#E5322A]/10 text-[#E5322A] rounded" title="Ekstre Gönder"><Share2 className="w-4 h-4" /></button>
                    <button data-testid={`cari-wa-${c.id}`} onClick={(e) => { e.stopPropagation(); shareWA(c); }} className="p-1.5 hover:bg-emerald-500/10 text-emerald-500 rounded" title="WhatsApp"><Send className="w-4 h-4" /></button>
                    <button data-testid={`cari-pay-${c.id}`} onClick={(e) => { e.stopPropagation(); setCardPay({ customerId: c.id, customerName: c.name, amount: Math.max(0, Number(c.balance || 0)) }); }} className="p-1.5 hover:bg-[#E5322A]/10 text-[#E5322A] rounded" title="Kredi Kartı ile Ödeme Yap"><CreditCard className="w-4 h-4" /></button>
                    <button data-testid={`cari-edit-${c.id}`} onClick={(e) => { e.stopPropagation(); setEditing(c); }} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded"><Pencil className="w-4 h-4" /></button>
                    {isAdmin && <button data-testid={`cari-delete-${c.id}`} onClick={(e) => { e.stopPropagation(); del(c); }} className="p-1.5 hover:bg-rose-500/10 text-rose-500 rounded"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center text-slate-500 py-8">{tr("Kayıt yok")}</td></tr>}
          </tbody>
        </table>
        <ZenPagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
        </StickyScrollWrapper>
      </div>
      {editing && <CustomerForm data={editing} onClose={() => setEditing(null)} onSave={save} />}
      {statementCust && <StatementDialog customer={statementCust} onClose={() => setStatementCust(null)} />}
      {shareCust && <StatementShareModal customer={shareCust} onClose={() => setShareCust(null)} />}
      {showImport && <BulkImportModal onClose={() => setShowImport(false)} onDone={load} />}
      {cardPay && (
        <CardPayModal
          customerId={cardPay.customerId}
          customerName={cardPay.customerName}
          defaultAmount={cardPay.amount}
          onClose={() => setCardPay(null)}
        />
      )}
    </div>
  );
}

// ── Mini Sparkline (SVG tabanlı, recharts gerektirmez) ──────────────────────
function sparkPath(data, w = 120, h = 28) {
  if (!data || data.length < 2) return "";
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 6) - 3;
    return `${x},${y}`;
  });
  return "M " + pts.join(" L ");
}
function MiniSparkline({ data, color = "#06b6d4", id = "sp", w = 120, h = 28 }) {
  const d = sparkPath(data, w, h);
  if (!d) return null;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" style={{ opacity: 0.7 }}>
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L ${w},${h} L 0,${h} Z`} fill={`url(#g-${id})`} />
      <path d={d} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── İki kolonlu bilgi satırı ───────────────────────────────────────────────
function InfoRow({ label, value, accent, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</dt>
      <dd className={`text-sm font-medium text-slate-700 dark:text-[#c8d6e5] ${mono ? "font-mono" : ""}`} style={accent ? { color: accent } : {}}>{value}</dd>
    </div>
  );
}

// ── Satır Girdi Grubu (tekrar eden input bloğunu küçültür) ───────────────────
function PricingInputs({ ptKey, fgKey, vals, onChange, isSubRow = false }) {
  const id = fgKey ? `${ptKey}-${fgKey}` : ptKey;
  return (
    <>
      <td className={`px-3 py-2 ${isSubRow ? "py-1.5" : ""}`}>
        <input type="number" min="0" max="100" step="0.5"
          value={vals.iskontoPct} onChange={e => onChange("iskontoPct", e.target.value)}
          placeholder="—"
          className={`w-[72px] text-xs font-mono bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-rose-500/60 transition-colors ${isSubRow ? "opacity-90" : ""}`}
          data-testid={`pricing-iskonto-${id}`}
        />
      </td>
      <td className={`px-3 py-2 ${isSubRow ? "py-1.5" : ""}`}>
        <input type="number" min="0" max="300" step="0.5"
          value={vals.marginPct} onChange={e => onChange("marginPct", e.target.value)}
          placeholder="—"
          className={`w-[72px] text-xs font-mono bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-sky-500/60 transition-colors ${isSubRow ? "opacity-90" : ""}`}
          data-testid={`pricing-margin-${id}`}
        />
      </td>
      <td className={`px-3 py-2 ${isSubRow ? "py-1.5" : ""}`}>
        <input type="number" min="0" step="0.01"
          value={vals.netPrice} onChange={e => onChange("netPrice", e.target.value)}
          placeholder="—"
          className={`w-[88px] text-xs font-mono bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/60 transition-colors ${isSubRow ? "opacity-90" : ""}`}
          data-testid={`pricing-netprice-${id}`}
        />
      </td>
      <td className={`px-2 py-2 ${isSubRow ? "py-1.5" : ""}`}>
        <select value={vals.currency} onChange={e => onChange("currency", e.target.value)}
          className="text-[11px] bg-white/5 border border-white/10 rounded-lg px-1.5 py-1.5 text-slate-300 focus:outline-none focus:border-sky-500/60 transition-colors w-[58px]"
          data-testid={`pricing-currency-${id}`}>
          <option>EUR</option><option>TL</option><option>USD</option>
        </select>
      </td>
    </>
  );
}

// ── Müşteri Bazlı Fiyatlandırma Sekmesi ─────────────────────────────────────
function CustomerPricingTab({ customer, onSave }) {
  const [globalMargins, setGlobalMargins] = useState({});
  const [rows, setRows] = useState({});
  const [fabricRows, setFabricRows] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [purchasePrices, setPurchasePrices] = useState({}); // { fabricGroupKey: {min, max, avg, cur} }
  const [eurToTl, setEurToTl] = useState(56);
  const [usdToTl, setUsdToTl] = useState(49);
  const [supplierDiscounts, setSupplierDiscounts] = useState({});

  // Şablon panosu: localStorage'dan oku
  const [clipboard, setClipboard] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zenErpPriceTemplate") || "null"); }
    catch { return null; }
  });

  const emptyVals = () => ({ iskontoPct: "", marginPct: "", netPrice: "", currency: "EUR" });

  const ruleToVals = (rules, ptKey, fgKey = "") => {
    const fixed  = rules.find(r => r.slug === ptKey && (r.fabricGroup || "") === fgKey && r.type === "fixed");
    const margin = rules.find(r => r.slug === ptKey && (r.fabricGroup || "") === fgKey && r.type === "margin");
    const disc   = rules.find(r => r.slug === ptKey && (r.fabricGroup || "") === fgKey && r.type === "discount");
    return {
      iskontoPct: disc   ? String(disc.val)   : "",
      marginPct:  margin ? String(margin.val) : "",
      netPrice:   fixed  ? String(fixed.val)  : "",
      currency:   fixed?.currency || "EUR",
    };
  };

  useEffect(() => {
    api.get("/settings?category=pricing_defaults").then(({ data }) => {
      const entry = (data || []).find(s => s.key === "global_margins");
      if (entry?.value) setGlobalMargins(entry.value);
      const sdEntry = (data || []).find(s => s.key === "supplier_discounts");
      if (sdEntry?.value) setSupplierDiscounts(sdEntry.value);
    }).catch(() => {});

    const rules = Array.isArray(customer.priceRules) ? customer.priceRules : [];

    // Ürün tipi seviyesi
    const newRows = {};
    PRICE_RULE_PRODUCT_TYPES.forEach(pt => { newRows[pt.key] = ruleToVals(rules, pt.key, ""); });
    setRows(newRows);

    // Kumaş grubu seviyesi
    const newFabric = {};
    PRICE_RULE_PRODUCT_TYPES.forEach(pt => {
      const groups = PRICE_RULE_FABRIC_GROUPS[pt.key] || [];
      if (!groups.length) return;
      newFabric[pt.key] = {};
      groups.forEach(fg => { newFabric[pt.key][fg.key] = ruleToVals(rules, pt.key, fg.key); });
    });
    setFabricRows(newFabric);

    // Alış fiyatları — önce kurları yükle, sonra döviz çevrimi yaparak hesapla
    api.get("/rates").then(({ data: ratesData }) => {
      const eurTl = ratesData?.eurToTl || 56;
      const usdTl = ratesData?.usdToTl || 49;
      if (ratesData?.eurToTl) setEurToTl(ratesData.eurToTl);
      if (ratesData?.usdToTl) setUsdToTl(ratesData.usdToTl);
      return api.get("/assets").then(({ data }) => {
        const assets = Array.isArray(data) ? data : [];
        const buckets = {};
        for (const a of assets) {
          if (!a || a.kind !== "fabric") continue;
          const ppRaw = Number(a.purchasePrice);
          if (!ppRaw || ppRaw <= 0) continue;
          const cur = (a.purchaseCurrency || "TL").toUpperCase();
          // Para birimi ne olursa olsun TL'ye çevir
          const ppTl = cur === "EUR" ? ppRaw * eurTl
                      : cur === "USD" ? ppRaw * usdTl
                      : ppRaw;
          const fg = a.fabricGroup || a.group || "";
          const sn = a.seriesName || "";
          // Natty Plise seri-spesifik key
          const key = (fg === "natty_plise" && sn) ? nattyPliseFabricGroupKey(sn) : fg;
          if (!key) continue;
          if (!buckets[key]) buckets[key] = [];
          buckets[key].push(ppTl);
        }
        const result = {};
        for (const [key, prices] of Object.entries(buckets)) {
          if (!prices.length) continue;
          const mn = Math.min(...prices);
          const mx = Math.max(...prices);
          const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
          result[key] = { min: mn, max: mx, avg };
        }
        setPurchasePrices(result);
      });
    }).catch(() => {});
  }, [customer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (ptKey, field, val) =>
    setRows(prev => ({ ...prev, [ptKey]: { ...(prev[ptKey] || emptyVals()), [field]: val } }));

  const updateFabric = (ptKey, fgKey, field, val) =>
    setFabricRows(prev => ({
      ...prev,
      [ptKey]: { ...(prev[ptKey] || {}), [fgKey]: { ...(prev[ptKey]?.[fgKey] || emptyVals()), [field]: val } },
    }));

  const toggleExpand = (key) =>
    setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const valsToRule = (ptKey, fgKey, vals) => {
    const net = parseFloat(vals.netPrice);
    const disc = parseFloat(vals.iskontoPct);
    const margin = parseFloat(vals.marginPct);
    if (!isNaN(net) && net > 0)    return { slug: ptKey, fabricGroup: fgKey, type: "fixed",    val: net,    currency: vals.currency };
    if (!isNaN(disc) && disc > 0)  return { slug: ptKey, fabricGroup: fgKey, type: "discount", val: disc,   currency: vals.currency };
    if (!isNaN(margin) && margin > 0) return { slug: ptKey, fabricGroup: fgKey, type: "margin", val: margin, currency: "EUR" };
    return null;
  };

  const save = async () => {
    setSaving(true);
    try {
      const newRules = [];
      // Ürün tipi seviyesi kuralları
      Object.entries(rows).forEach(([ptKey, vals]) => {
        const r = valsToRule(ptKey, "", vals);
        if (r) newRules.push(r);
      });
      // Kumaş grubu seviyesi kuralları
      Object.entries(fabricRows).forEach(([ptKey, groups]) => {
        Object.entries(groups).forEach(([fgKey, vals]) => {
          const r = valsToRule(ptKey, fgKey, vals);
          if (r) newRules.push(r);
        });
      });
      await api.put(`/customers/${customer.id}`, { ...customer, priceRules: newRules });
      onSave({ priceRules: newRules });
      toast.success("Fiyatlandırma kaydedildi");
    } catch (e) { toast.error("Kayıt hatası"); }
    setSaving(false);
  };

  const handleCopyTemplate = () => {
    const tpl = { rows, fabricRows, sourceName: customer.name, savedAt: new Date().toLocaleString("tr-TR") };
    localStorage.setItem("zenErpPriceTemplate", JSON.stringify(tpl));
    setClipboard(tpl);
    toast.success(`"${customer.name}" şablon olarak kopyalandı`);
  };

  const handleApplyTemplate = () => {
    if (!clipboard) { toast.error("Kopyalanmış şablon bulunamadı"); return; }
    if (!window.confirm(`"${clipboard.sourceName}" şablonu uygulanacak (${clipboard.savedAt}). Mevcut kurallar silinecek. Onaylıyor musunuz?`)) return;
    setRows(clipboard.rows || {});
    setFabricRows(clipboard.fabricRows || {});
    toast.success("Şablon uygulandı — Kaydet butonuna basarak onaylayın");
  };

  const hasAnyValue = (vals) => vals && (vals.iskontoPct || vals.marginPct || vals.netPrice);

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white">Müşteri Bazlı Fiyatlandırma</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Ürün grubuna genel iskonto/marj girin. Ok ile genişletip kumaş serisi bazlı ek kural ekleyin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Şablon Kopyala */}
          <button onClick={handleCopyTemplate} title="Bu müşterinin tüm fiyat kurallarını panoya kopyala"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 transition-colors">
            <Copy className="w-3 h-3" />
            Şablon Kopyala
          </button>
          {/* Şablon Uygula — sadece panoda şablon varsa göster */}
          {clipboard && (
            <button onClick={handleApplyTemplate} title={`"${clipboard.sourceName}" şablonunu uygula`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 transition-colors">
              <ClipboardList className="w-3 h-3" />
              Şablonu Uygula
            </button>
          )}
          <button onClick={save} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-sky-600 hover:bg-sky-500 text-white transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Kaydet
          </button>
        </div>
      </div>

      {/* Tablo */}
      <div className="zen-pricing-panel rounded-2xl overflow-hidden border border-white/8" style={{ backdropFilter: "blur(12px)" }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-white/8" style={{ background: "rgba(255,255,255,0.03)" }}>
              <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 w-52">Ürün Grubu</th>
              <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-emerald-400/60" title="Ortalama alış fiyatı (TL/m²)">Alış (TL/m²)</th>
              <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-rose-400/70">İskonto %</th>
              <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-sky-400/70">Kâr Marjı %</th>
              <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-amber-400/70">Net Fiyat</th>
              <th className="text-center px-2 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">Döviz</th>
            </tr>
          </thead>
          <tbody>
            {PRICE_RULE_PRODUCT_TYPES.map((pt, ptIdx) => {
              // Zip ve Sineklik için alt kademe gösterilmez
              const NO_EXPAND = new Set(["sineklik"]);
              const hasGroups = !NO_EXPAND.has(pt.key) && (PRICE_RULE_FABRIC_GROUPS[pt.key] || []).length > 0;
              const isOpen = expanded.has(pt.key);
              const vals = rows[pt.key] || emptyVals();
              const gm = globalMargins[pt.key];
              const hasPtVal = hasAnyValue(vals);
              const isLast = ptIdx === PRICE_RULE_PRODUCT_TYPES.length - 1;

              return (
                <React.Fragment key={pt.key}>
                  {/* Ürün tipi ana satırı */}
                  <tr
                    className={`border-t border-white/5 transition-colors ${isOpen ? "bg-white/3" : "hover:bg-white/2"}`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {hasGroups ? (
                          <button
                            onClick={() => toggleExpand(pt.key)}
                            className="w-5 h-5 rounded-md flex items-center justify-center transition-all hover:bg-white/10 text-slate-400 hover:text-white shrink-0"
                            data-testid={`pricing-expand-${pt.key}`}
                          >
                            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          </button>
                        ) : (
                          <div className="w-5 shrink-0" />
                        )}
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: pt.color }} />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-700 dark:text-slate-200 leading-tight truncate">{pt.label}</div>
                          {pt.sub && <div className="text-[10px] text-slate-500 leading-tight truncate">{pt.sub}</div>}
                        </div>
                        {gm > 0 && !hasPtVal && (
                          <span className="ml-1 text-[9px] text-slate-600 shrink-0">dflt %{gm}</span>
                        )}
                        {hasPtVal && (
                          <span className="ml-1 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" title="Kural aktif" />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {(() => {
                        const pp = purchasePrices[pt.key];
                        if (!pp) {
                          const sd = supplierDiscounts[pt.key];
                          if (sd > 0) return (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-600 dark:text-sky-400 font-mono bg-sky-50 dark:bg-sky-900/20 px-1.5 py-0.5 rounded">
                              %{sd} isk
                            </span>
                          );
                          return <span className="text-[10px] text-slate-500">—</span>;
                        }
                        const tlTxt = pp.min === pp.max
                          ? `${pp.min.toLocaleString("tr-TR")} TL`
                          : `${pp.min.toLocaleString("tr-TR")}–${pp.max.toLocaleString("tr-TR")} TL`;
                        const eurMin = (pp.min / eurToTl).toFixed(2);
                        const eurMax = (pp.max / eurToTl).toFixed(2);
                        const eurTxt = eurMin === eurMax ? `€${eurMin}` : `€${eurMin}–€${eurMax}`;
                        return (
                          <div className="flex flex-col items-center gap-0">
                            <span className="text-[10px] text-emerald-500/70 font-mono leading-tight">{tlTxt}</span>
                            <span className="text-[9px] text-slate-500 font-mono leading-tight">{eurTxt}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <PricingInputs
                      ptKey={pt.key} fgKey="" vals={vals}
                      onChange={(field, val) => updateRow(pt.key, field, val)}
                    />
                  </tr>

                  {/* Kumaş grubu alt satırları */}
                  {hasGroups && isOpen && (PRICE_RULE_FABRIC_GROUPS[pt.key] || []).map((fg, fgIdx) => {
                    // Group header satırı — girdi yok, sadece bölücü başlık
                    if (fg.isGroupHeader) {
                      return (
                        <tr key={`${pt.key}-${fg.key}-header`} style={{ background: "rgba(255,255,255,0.02)" }}>
                          <td colSpan={6} className="pl-7 pr-3 py-1 pt-2">
                            <span className="text-[9px] font-bold uppercase tracking-widest opacity-50" style={{ color: pt.color }}>
                              {fg.label.replace(/═/g, "").trim()}
                            </span>
                          </td>
                        </tr>
                      );
                    }
                    const fgVals = fabricRows[pt.key]?.[fg.key] || emptyVals();
                    const hasFgVal = hasAnyValue(fgVals);
                    const isLastFg = fgIdx === (PRICE_RULE_FABRIC_GROUPS[pt.key] || []).length - 1;
                    return (
                      <tr key={`${pt.key}-${fg.key}`}
                        className={`border-t border-white/3 transition-colors hover:bg-white/2 ${isLastFg && isLast ? "" : ""}`}
                        style={{ background: "rgba(255,255,255,0.015)" }}
                      >
                        <td className="pl-10 pr-3 py-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-px h-3 rounded-full opacity-30" style={{ backgroundColor: pt.color }} />
                            <span className="text-[11px] text-slate-400">{fg.label}</span>
                            {hasFgVal && (
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" title="Kural aktif" />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {(() => {
                            const pp = purchasePrices[fg.key];
                            if (!pp) {
                              const sd = supplierDiscounts[pt.key];
                              if (sd > 0) return (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-600 dark:text-sky-400 font-mono bg-sky-50 dark:bg-sky-900/20 px-1.5 py-0.5 rounded">
                                  %{sd} isk
                                </span>
                              );
                              return <span className="text-[10px] text-slate-500">—</span>;
                            }
                            const minTl = pp.min;
                            const maxTl = pp.max;
                            const minEur = (minTl / eurToTl).toFixed(2);
                            const maxEur = (maxTl / eurToTl).toFixed(2);
                            const tlTxt = minTl === maxTl
                              ? `${minTl.toLocaleString("tr-TR")} TL`
                              : `${minTl.toLocaleString("tr-TR")}–${maxTl.toLocaleString("tr-TR")} TL`;
                            const eurTxt = minEur === maxEur
                              ? `€${minEur}`
                              : `€${minEur}–€${maxEur}`;
                            return (
                              <div className="flex flex-col items-center gap-0">
                                <span className="text-[10px] text-emerald-400/80 font-mono leading-tight">{tlTxt}</span>
                                <span className="text-[9px] text-slate-500 font-mono leading-tight">{eurTxt}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <PricingInputs
                          ptKey={pt.key} fgKey={fg.key} vals={fgVals}
                          onChange={(field, val) => updateFabric(pt.key, fg.key, field, val)}
                          isSubRow
                        />
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Öncelik notu */}
      <div className="flex items-start gap-2 px-4 py-3 rounded-xl border text-[11px] text-sky-400"
        style={{ background: "rgba(56,189,248,0.04)", borderColor: "rgba(56,189,248,0.15)" }}>
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          <strong>Öncelik:</strong> Kumaş Serisi Kuralı &gt; Ürün Grubu Kuralı &gt; Global Varsayılan.
          &nbsp;<strong>İskonto %</strong> liste fiyatından indirim (Zip için ideal).
          &nbsp;<strong>Kâr Marjı %</strong> alış maliyetine ekleme (Stor/Zebra için ideal).
          &nbsp;<strong>Yeşil nokta</strong> = aktif kural var.
        </span>
      </div>
    </div>
  );
}

function CustomerFullPage({ customer: initC, onClose }) {
  const { tr } = useContext(UICtx);
  const { user } = useContext(AuthCtx);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showMove, setShowMove] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [c, setC] = useState(initC);
  const [rates, setRates] = useState({ eurToTl: 38, usdToTl: 34 });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [expandedMovKey, setExpandedMovKey] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showBalanceEdit, setShowBalanceEdit] = useState(false);
  const [viewingOrderId, setViewingOrderId] = useState(null);
  const isAdmin = user?.role === "admin";
  const [activeView, setActiveView] = useState("bilgi");
  const moreRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [o, p, i, cs, r] = await Promise.all([
        api.get("/orders"), api.get("/payments"), api.get("/invoices"),
        api.get("/customers"), api.get("/rates"),
      ]);
      setOrders(o.data); setPayments(p.data); setInvoices(i.data);
      if (r.data) setRates(r.data);
      const fresh = cs.data.find(x => x.id === initC.id);
      if (fresh) setC(fresh);
    })();
  }, [refresh, initC.id]);

  useEffect(() => {
    const handle = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [onClose]);

  useEffect(() => {
    const handle = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setShowMoreMenu(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ── Computed values ────────────────────────────────────────────

  const deletePaymentFromStatement = async (pay) => {
    if (!window.confirm(`"${pay.note || pay.kind}" hareketi silinsin mi? Bakiye güncellenir.`)) return;
    try {
      await api.delete(`/payments/${pay.id}`);
      setPayments(prev => prev.filter(p => p.id !== pay.id));
      setExpandedMovKey(null);
      toast.success("Hareket silindi");
      const fresh = (await api.get("/customers")).data.find(x => x.id === c.id);
      if (fresh) setC(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const custOrders = useMemo(() =>
    orders.filter(o => o.customerId === c.id).sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")),
    [orders, c.id]);
  const salesOrders = custOrders.filter(o => o.status !== "teklif");
  const purchaseInvoices = useMemo(() =>
    invoices.filter(inv => inv.customerId === c.id && (inv.type === "alis" || inv.type === "alis_iade")),
    [invoices, c.id]);
  const activeSalesOrders = salesOrders.filter(o => ["onay_bekliyor", "imalata_hazir", "imalatta", "hazir", "kargolanacak"].includes(o.status));
  const activeSalesTotal = activeSalesOrders.reduce((s, o) => s + convertPrice(o.total || 0, o.currency, c.currency, rates), 0);
  const custPayments = useMemo(() =>
    payments.filter(p => p.customerId === c.id).sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [payments, c.id]);

  // ── Toplam ödeme (tahsilat + borc_tahakkuku) ──────────────────────
  const totalPaid = useMemo(() =>
    custPayments
      .filter(p => ["tahsilat", "borc_tahakkuku"].includes(p.kind || "tahsilat"))
      .reduce((s, p) => s + convertPrice(p.amount || 0, p.currency || c.currency, c.currency, rates), 0),
    [custPayments, c.currency, rates]);

  // ── Toplam alışveriş (tüm onaylı siparişler) ──────────────────────
  const totalPurchases = useMemo(() =>
    salesOrders.reduce((s, o) => s + convertPrice(o.total || 0, o.currency, c.currency, rates), 0),
    [salesOrders, c.currency, rates]);

  // ── Son 6 ay aylık ciro ────────────────────────────────────────
  const last6Months = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return months;
  }, []);
  const monthlyRevenue = useMemo(() =>
    last6Months.map(ym =>
      salesOrders.filter(o => (o.createdAt || "").startsWith(ym) && o.status !== "iptal")
        .reduce((s, o) => s + convertPrice(o.total || 0, o.currency, c.currency, rates), 0)),
    [salesOrders, last6Months, c.currency, rates]);
  const totalRevenue6m = monthlyRevenue.reduce((a, b) => a + b, 0);

  const metricMax = Math.max(Math.abs(c.balance || 0), totalPurchases, totalPaid, 1);

  const METRICS = [
    { label: "Toplam Bakiye", value: c.balance || 0, color: (c.balance || 0) > 0 ? "#f87171" : "#34d399", badge: (c.balance || 0) > 0 ? "B" : "A", sparkData: monthlyRevenue, sparkId: "mb" },
    { label: "Toplam Alışveriş", value: totalPurchases, color: "#f59e0b", badge: null, sparkData: monthlyRevenue, sparkId: "ac" },
    { label: "Toplam Ödeme", value: totalPaid, color: "#06b6d4", badge: null, sparkData: monthlyRevenue.map(v => v * 0.8), sparkId: "tp" },
  ];
  const invoicedOrderIds = useMemo(() => new Set(invoices.filter(i => i.customerId === c.id).map(i => i.orderId || "").filter(Boolean)), [invoices, c.id]);

  // ── Cari ekstre hareketleri ────────────────────────────────────
  const allMovements = useMemo(() => {
    const arr = [];
    const orderById = Object.fromEntries(orders.map(o => [o.id, o]));
    orders.filter(o => o.customerId === c.id && o.status !== "teklif" && o.status !== "iptal").forEach(o => {
      const raw = (o.approvedAt || o.createdAt || "").slice(0, 10);
      arr.push({ isoDate: raw, date: fmtDate(o.approvedAt || o.createdAt || ""), type: "Sipariş", ref: ono(o), note: `${(o.items || [])[0]?.product || ""}${o.items?.length > 1 ? ` +${o.items.length - 1}` : ""}`, debit: o.total, credit: 0, currency: o.currency, sourceObj: o, sourceType: "order" });
    });
    invoices.filter(inv => inv.customerId === c.id).forEach(inv => {
      const isDebit = inv.type === "satis" || inv.type === "alis";
      const hasLinkedOrder = !!inv.orderId;
      // Siparişe bağlı faturada sadece KDV tutarı gösterilir (sipariş zaten borç olarak kayıtlı)
      const displayAmount = hasLinkedOrder ? (inv.vatAmount || 0) : (inv.total || 0);
      const linkedOrder = hasLinkedOrder ? orderById[inv.orderId] : null;
      const lbl = hasLinkedOrder
        ? "KDV Faturası"
        : ({ satis: "Satış Faturası", alis: "Alış Faturası", satis_iade: "Satış İadesi", alis_iade: "Alış İadesi", irsaliye: "İrsaliye" }[inv.type] || "Fatura");
      const noteText = hasLinkedOrder
        ? `${linkedOrder ? ono(linkedOrder) : inv.orderId} sipariş KDV'si`
        : `${(inv.items || []).length} kalem`;
      arr.push({ isoDate: (inv.issueDate || "").slice(0, 10), date: fmtDate(inv.issueDate), type: lbl, ref: inv.docNo || inv.id, note: noteText, debit: isDebit ? displayAmount : 0, credit: !isDebit ? displayAmount : 0, currency: inv.currency, sourceObj: inv, sourceType: "invoice" });
    });
    payments.filter(p => p.customerId === c.id).forEach(p => {
      const kindLbl = { tahsilat: "Tahsilat", tediye: "Tediye", alacak_tahakkuku: "Alacak Th.", borc_tahakkuku: "Borç Th." }[p.kind || "tahsilat"];
      const methodLbl = { havale: "Havale", nakit: "Nakit", banka: "Banka", kart: "Kart", cek: "Çek" }[p.method] || p.method;
      const isCredit = ["tahsilat", "borc_tahakkuku"].includes(p.kind || "tahsilat");
      arr.push({ isoDate: (p.date || "").slice(0, 10), date: fmtDate(p.date), type: kindLbl, ref: p.id, note: `${methodLbl}${p.note ? ` · ${p.note}` : ""}`, debit: !isCredit ? p.amount : 0, credit: isCredit ? p.amount : 0, currency: p.currency, sourceObj: p, sourceType: "payment" });
    });
    return arr.sort((a, b) => (b.isoDate || "").localeCompare(a.isoDate || ""));
  }, [orders, payments, invoices, c.id]);

  const filtered = allMovements.filter(m => (!from || m.isoDate >= from) && (!to || m.isoDate <= to));
  // withBalance: eskiden yeniye sıralı (artan) hesapla, doğru birikimli bakiye için
  const filteredAsc = [...filtered].sort((a, b) => (a.isoDate || "").localeCompare(b.isoDate || ""));
  let running = convertPrice(c.openingBalance || 0, c.currency, c.currency, rates);
  const withBalanceAsc = filteredAsc.map(m => {
    const dInCur = convertPrice(m.debit || 0, m.currency, c.currency, rates);
    const cInCur = convertPrice(m.credit || 0, m.currency, c.currency, rates);
    running += dInCur - cInCur;
    return { ...m, dInCur, cInCur, balance: running };
  });
  // Ekranda yeniden eskiye göster (ters çevir)
  const withBalance = [...withBalanceAsc].reverse();

  const generate = () => {
    generateStatementPDF(c, orders, payments, { from: from || null, to: to || null, invoices });
    toast.success("Ekstre yazdırma başlatıldı");
  };

  // ── Style helpers ──────────────────────────────────────────────
  const STATUS_COLOR = { teklif: "slate", onaylandi: "sky", uretimde: "amber", teslim: "emerald", odendi: "emerald", iptal: "rose" };
  const TYPE_COLOR_MAP = { "Yurt İçi Müşteri": "#0ea5e9", "Yurt Dışı Müşteri": "#10b981", "Tedarikçi": "#a855f7", "Çalışan": "#f59e0b" };
  const typeColor = TYPE_COLOR_MAP[c.type] || "#0ea5e9";

  const pct = (v) => Math.min(100, (Math.abs(v) / metricMax) * 100);

  const TABS = [
    { id: "bilgi", label: "Cari Bilgileri", icon: Users },
    { id: "hareketler", label: "Hareketler", icon: History },
    { id: "ekstre", label: "Cari Ekstresi", icon: Receipt },
    { id: "satis", label: "Satış Siparişleri", icon: ShoppingCart },
    { id: "alis", label: "Alış Siparişleri", icon: FileDown },
    { id: "fiyatlandirma", label: "Fiyatlandırma", icon: TrendingUp },
  ];

  const balanceTL = c.currency === "TL" ? (c.balance || 0) : convertPrice(c.balance || 0, c.currency, "TL", rates);
  const balanceEUR = c.currency === "EUR" ? (c.balance || 0) : convertPrice(c.balance || 0, c.currency, "EUR", rates);

  return (
    <div className="fixed inset-0 z-40 bg-[#070c17] overflow-y-auto flex flex-col" data-testid="customer-fullpage">
      {/* Ödeme düzenleme modal */}
      {editingPayment && (
        <PaymentEditModal
          payment={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSaved={async () => {
            const fresh = (await api.get(`/payments?customerId=${c.id}`)).data;
            if (fresh) setPayments(prev => [...prev.filter(p => p.customerId !== c.id), ...fresh]);
            const fc = (await api.get("/customers")).data.find(x => x.id === c.id);
            if (fc) setC(fc);
          }}
        />
      )}

      {showBalanceEdit && (
        <BalanceEditModal
          customer={c}
          onClose={() => setShowBalanceEdit(false)}
          onSaved={async (newC) => {
            setC(newC);
            setShowBalanceEdit(false);
          }}
        />
      )}

      {/* ── ÜSTÜNDEKI BAR: Başlık + Breadcrumb + Butonlar ─────────────── */}
      <div className="sticky top-0 z-20 bg-[#0a1020]/97 backdrop-blur-xl border-b border-white/5">
        <div className="px-5 py-2.5 flex items-center justify-between gap-4 max-w-[1600px] mx-auto">

          {/* Sol: Geri + Başlık */}
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="customer-detail-back" onClick={onClose}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors group shrink-0">
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span className="hidden sm:block">Cari Listesi</span>
            </button>
            <span className="text-slate-700 hidden sm:block">|</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-500 font-semibold shrink-0">Cari Detay: <span className="text-[#E5322A] font-mono">{c.id}</span></span>
                <span className="text-sm font-black text-white truncate">{c.name}</span>
              </div>
              {c.address && (
                <div className="text-[10px] text-slate-600 tracking-wide">{c.address.split(/[,\/]/).map(p => p.trim()).filter(Boolean).slice(0, 3).join(" / ")}</div>
              )}
            </div>
          </div>

          {/* Sağ: Butonlar */}
          <div className="flex items-center gap-1.5 shrink-0">
            <PrintTemplateMenu page="customers" data={{ name: c.name, code: c.id, balance: c.balance, phone: c.phone, email: c.email, city: (c.address || "").split(/[\/,]/)[0] || "", type: c.type, currency: c.currency, taxNo: c.taxNo, address: c.address }} buttonLabel="Şablon Yazdır" />
            <button onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-sky-600 hover:bg-sky-500 text-white transition-colors">
              <Pencil className="w-3 h-3" /> DÜZENLE
            </button>
            <button data-testid="customer-detail-yeni-odeme" onClick={() => setShowMove(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#1a2a44] hover:bg-[#213255] text-sky-300 border border-sky-700/40 transition-colors">
              <Plus className="w-3 h-3" /> Hareket Ekle
            </button>
            <div ref={moreRef} className="relative">
              <button onClick={() => setShowMoreMenu(v => !v)}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors border border-white/5">
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-1.5 w-48 zen-panel p-1.5 z-50 shadow-2xl border border-white/10">
                  <button data-testid="customer-detail-ekstre-gonder" onClick={() => { setShowShare(true); setShowMoreMenu(false); }}
                    className="w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-white/5 flex items-center gap-2 text-slate-300">
                    <Send className="w-3.5 h-3.5 text-slate-500" /> Ekstre Gönder
                  </button>
                  <button onClick={() => { generate(); setShowMoreMenu(false); }}
                    className="w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-white/5 flex items-center gap-2 text-slate-300">
                    <Printer className="w-3.5 h-3.5 text-slate-500" /> Ekstre Yazdır
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── 4 METRİK KART (Referans görseldeki progress bar yapısı + Liquid Glass) ── */}
      <div className="bg-[#0a1020] border-b border-white/5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 max-w-[1600px] mx-auto divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          {METRICS.map((m, idx) => (
            <div key={idx} className="relative px-5 py-4 overflow-hidden group hover:bg-white/2 transition-colors">
              {/* Sparkline (arka plan) */}
              <div className="absolute bottom-0 right-0 pointer-events-none">
                <MiniSparkline data={m.sparkData} color={m.color} id={m.sparkId} w={130} h={36} />
              </div>
              {/* Label */}
              <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: `${m.color}99` }}>{m.label}</div>
              {/* Value */}
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-xl font-black" style={{ color: m.color }}>{fmt(Math.abs(m.value), c.currency)}</span>
                {m.badge && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border" style={{ color: m.color, borderColor: `${m.color}40`, background: `${m.color}15` }}>{m.badge}</span>
                )}
              </div>
              {/* Progress bar */}
              <div className="w-full h-[2px] rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct(m.value)}%`, background: `linear-gradient(90deg, ${m.color}80, ${m.color})` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── SEKMELER ────────────────────────────────────────────────────── */}
      <div className="bg-[#0a1020] border-b border-white/7 px-5 max-w-[1600px] w-full mx-auto">
        <div className="flex gap-0">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`cfp-tab-${id}`} onClick={() => setActiveView(id)}
              className={`relative flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-all ${
                activeView === id
                  ? "border-sky-400 text-sky-300 bg-sky-400/5"
                  : "border-transparent text-slate-500 hover:text-slate-300 hover:bg-white/2"
              }`}>
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:block">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── İÇERİK ALANI ──────────────────────────────────────────────── */}
      <div className="flex-1 px-5 py-5 max-w-[1600px] w-full mx-auto">

        {/* ────── CARİ BİLGİLERİ ──────────────────────────────────────── */}
        {activeView === "bilgi" && (
          <div className="space-y-1">

            {/* Üst satır: Cari Grubu, Durum, Sınıf */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 p-5 rounded-xl bg-white/2 border border-white/5 mb-4">
              {c.group && <InfoRow label="Cari Grubu" value={c.group} />}
              <InfoRow label="Cari Kodu" value={c.id} accent="#E5322A" mono />
              <InfoRow label="Cari Adı" value={c.name} />
              <InfoRow label="Para Birimi" value={c.currency || "TL"} />
              <InfoRow label="Cari Durumu" value="Limitsiz" />
              {c.type && <InfoRow label="Tipi" value={c.type} />}
              {(c.discountPercent || 0) > 0 && <InfoRow label="Genel İskonto" value={`%${c.discountPercent}`} />}
            </div>

            {/* Fatura Bilgileri */}
            <div className="rounded-xl border border-white/5 bg-white/2 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 bg-white/2">
                <FileText className="w-3.5 h-3.5 text-sky-400" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Fatura Bilgileri</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4 p-5">
                <InfoRow label="Vergi Dairesi" value={c.taxOffice || "—"} />
                <InfoRow label="Vergi Numarası" value={c.taxNo || "—"} mono />
                <InfoRow label="İhracat/KDV İstisnası" value={c.vatExemptEligible ? "Seçenek Sunuluyor" : "Kapalı"} accent={c.vatExemptEligible ? "#10b981" : undefined} />
                {c.vatExemptEligible && c.vatExemptNote && <InfoRow label="İstisna Notu" value={c.vatExemptNote} />}
                <InfoRow label="Ödeme Vade Günü" value={c.paymentDueDays != null ? `${c.paymentDueDays} Gün` : "0 Gün"} />
                {c.eInvoiceType && <InfoRow label="E-Fatura Senaryosu" value={c.eInvoiceType} />}
                {c.invoiceCode && <InfoRow label="Fatura Cari Kodu" value={c.invoiceCode} mono />}
              </div>
            </div>

            {/* İletişim Bilgileri */}
            <div className="rounded-xl border border-white/5 bg-white/2 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 bg-white/2">
                <Phone className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">İletişim Bilgileri</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4 p-5">
                <InfoRow label="Yetkili" value={c.contactName || "—"} />
                <InfoRow label="Yetkili Tel" value={c.contactPhone || "—"} />
                <InfoRow label="Telefon" value={c.phone || "—"} />
                <InfoRow label="E-Posta" value={c.email || "—"} />
                <InfoRow label="Adres" value={c.address || "—"} />
                {c.postalCode && <InfoRow label="Posta Kodu" value={c.postalCode} mono />}
              </div>
            </div>

            {/* Diğer Detaylar */}
            <div className="rounded-xl border border-white/5 bg-white/2 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 bg-white/2">
                <ClipboardList className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Diğer Detaylar</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4 p-5">
                {c.notes && <div className="col-span-2 md:col-span-4"><InfoRow label="Notlar" value={c.notes} /></div>}
                {(c.priceRules || []).length > 0 && (
                  <div className="col-span-2 md:col-span-4">
                    <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Özel Fiyat Kuralları</dt>
                    <div className="flex flex-wrap gap-2">
                      {(c.priceRules || []).map((r, i) => (
                        <span key={i} className="text-[11px] px-2.5 py-1 rounded-lg border border-violet-500/20 bg-violet-500/8 text-violet-300">
                          {r.slug}{r.fabricGroup ? ` · ${r.fabricGroup}` : ""} → {r.type === "fixed" ? `${fmt(r.val, r.currency || "EUR")}/m²` : `%${r.val} iskonto`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {Object.keys(c.productDiscounts || {}).filter(k => (c.productDiscounts || {})[k] > 0).length > 0 && (
                  <div className="col-span-2 md:col-span-4">
                    <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Ürün İskontoları</dt>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(c.productDiscounts || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                        <span key={k} className="text-[11px] px-2.5 py-1 rounded-lg border border-sky-500/20 bg-sky-500/8 text-sky-300 capitalize">{k}: %{v}</span>
                      ))}
                    </div>
                  </div>
                )}
                {c.dealerGroupId && <InfoRow label="Bayi Grubu" value={c.dealerGroupId} mono />}
                {!c.notes && (c.priceRules || []).length === 0 && Object.keys(c.productDiscounts || {}).filter(k => (c.productDiscounts || {})[k] > 0).length === 0 && !c.dealerGroupId && (
                  <div className="col-span-4 text-xs text-slate-600 italic py-2">Ek detay girilmemiş</div>
                )}
              </div>
            </div>

            {/* Bakiye özeti */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              <div className="rounded-xl border border-white/5 bg-white/2 p-4">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Net Bakiye ({c.currency})</dt>
                <div className="text-[10px] text-slate-500/80 mb-1.5 leading-snug">Pozitif (+): müşteri size borçlu · Negatif (−): sizden alacağı var · Sıfır: hesap kapalı</div>
                <div className="flex items-center justify-between">
                  <dd className={`text-xl font-black ${(c.balance || 0) > 0 ? "text-rose-400" : (c.balance || 0) < 0 ? "text-emerald-400" : "text-slate-400"}`}>{fmt(c.balance || 0, c.currency)}</dd>
                  {isAdmin && (
                    <button
                      data-testid="balance-edit-btn"
                      onClick={() => setShowBalanceEdit(true)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-white/8 hover:bg-amber-500/20 text-amber-400 border border-white/10 transition-colors"
                      title="Bakiyeyi düzenle veya sıfırla"
                    >
                      <Pencil className="w-3 h-3" /> Düzenle
                    </button>
                  )}
                </div>
                {(c.openingBalance || 0) !== 0 && (
                  <div className="text-[10px] text-slate-600 mt-1">Açılış: {fmt(c.openingBalance || 0, c.currency)}</div>
                )}
              </div>
              {c.currency !== "TL" && (
                <div className="rounded-xl border border-white/5 bg-white/2 p-4">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">TL Karşılığı</dt>
                  <dd className="text-xl font-black text-slate-300">{fmt(balanceTL, "TL")}</dd>
                </div>
              )}
              {c.currency !== "EUR" && (
                <div className="rounded-xl border border-white/5 bg-white/2 p-4">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">EUR Karşılığı</dt>
                  <dd className="text-xl font-black text-slate-300">{fmt(balanceEUR, "EUR")}</dd>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ────── HAREKETLER ────────────────────────────────────────────── */}
        {activeView === "hareketler" && (
          <div className="space-y-1.5 max-w-3xl">
            {allMovements.length === 0 && <div className="text-center text-slate-500 py-16 text-sm">Hareket bulunamadı</div>}
            {allMovements.map((m, i) => {
              const isDebit = m.debit > 0;
              const col = isDebit ? "#f87171" : "#34d399";
              const isPayment = m.sourceType === "payment";
              const canEdit = isPayment && !m.sourceObj?.isLegacy && isAdmin;
              return (
                <div
                  key={i}
                  onDoubleClick={() => canEdit && setEditingPayment(m.sourceObj)}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl border border-white/5 bg-white/2 hover:bg-white/4 transition-colors ${canEdit ? "cursor-pointer" : ""}`}
                  title={canEdit ? "Düzenlemek için çift tıklayın" : ""}
                >
                  <div className="w-1.5 h-8 rounded-full shrink-0" style={{ background: col }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-200">{m.type}</span>
                      <span className="text-[10px] font-mono text-slate-600">{m.ref}</span>
                      <span className="text-[10px] text-slate-600">{m.date}</span>
                    </div>
                    {m.note && <div className="text-[11px] text-slate-500 truncate mt-0.5">{m.note}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold" style={{ color: col }}>
                      {isDebit ? "+" : "-"}{fmt(isDebit ? m.debit : m.credit, m.currency)}
                    </div>
                    {m.currency !== c.currency && (
                      <div className="text-[10px] text-slate-600">{fmt(isDebit ? convertPrice(m.debit, m.currency, c.currency, rates) : convertPrice(m.credit, m.currency, c.currency, rates), c.currency)}</div>
                    )}
                  </div>
                  {/* Hover'da çıkan butonlar — sadece ödeme hareketleri */}
                  {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        data-testid={`hareket-duzenle-${m.sourceObj?.id}`}
                        onClick={e => { e.stopPropagation(); setEditingPayment(m.sourceObj); }}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-cyan-500/20 text-cyan-400 border border-white/10 transition-colors"
                        title="Düzenle"
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        data-testid={`hareket-sil2-${m.sourceObj?.id}`}
                        onClick={e => { e.stopPropagation(); deletePaymentFromStatement(m.sourceObj); }}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-rose-500/20 text-rose-400 border border-white/10 transition-colors"
                        title="Sil"
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ────── CARİ EKSTRESİ ─────────────────────────────────────────── */}
        {activeView === "ekstre" && (
          <div className="space-y-4">
            {/* ── Özet: Toplam Borç + Aktif Siparişler ──────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Toplam Borç Kartı */}
              <div className={`rounded-xl border p-4 ${(c.balance || 0) > 0 ? "border-rose-500/30 bg-rose-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                  {(c.balance || 0) > 0 ? "Toplam Borç (Müşteri Borçlu)" : "Toplam Bakiye (Müşteri Alacaklı)"}
                </div>
                <div className={`text-2xl font-black zen-mono ${(c.balance || 0) > 0 ? "text-rose-400" : "text-emerald-400"}`}>
                  {fmt(Math.abs(c.balance || 0), c.currency)}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Açılış: {fmt(c.openingBalance || 0, c.currency)} · Cari bakiye
                </div>
              </div>
              {/* Aktif Siparişler Kartı */}
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-400/80 mb-1">
                  Aktif Siparişler ({activeSalesOrders.length} adet)
                </div>
                <div className="text-2xl font-black zen-mono text-amber-400">
                  {fmt(activeSalesTotal, c.currency)}
                </div>
                {activeSalesOrders.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                    {activeSalesOrders.map(o => (
                      <div key={o.id} className="flex items-center justify-between text-[10px] text-slate-400">
                        <span className="zen-mono text-amber-300">{ono(o)}</span>
                        <span className="text-slate-300 font-semibold">{fmt(convertPrice(o.total || 0, o.currency, c.currency, rates), c.currency)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Başlangıç</label><input type="date" className="zen-input !py-1.5 !text-sm" value={from} onChange={e => setFrom(e.target.value)} /></div>
              <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Bitiş</label><input type="date" className="zen-input !py-1.5 !text-sm" value={to} onChange={e => setTo(e.target.value)} /></div>
              <button onClick={generate} className="zen-btn-ghost !text-xs"><Printer className="w-4 h-4" /> Yazdır</button>
              <button onClick={() => {
                const header = ["Tarih", "Tur", "Referans", "Aciklama", "Borc", "Alacak", "Bakiye"].join(";");
                const lines = withBalance.map(m => [fmtDate(m.date), (m.type || ""), (m.ref || ""), (m.note || "").replace(/;/g, ","), m.debit || "", m.credit || "", m.balance].join(";"));
                const csv = "\ufeff" + header + "\n" + lines.join("\n");
                const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" })); a.download = `Ekstre-${c.name.replace(/\s+/g, "_")}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                toast.success("CSV indirildi");
              }} className="zen-btn-ghost !text-xs"><FileDown className="w-4 h-4" /> CSV</button>
            </div>
            <div className="zen-panel !p-0 overflow-x-auto">
              <table className="zen-table !text-xs">
                <thead><tr><th>Tarih</th><th>Tür</th><th>Referans</th><th>Açıklama</th><th className="text-right">Borç</th><th className="text-right">Alacak</th><th className="text-right">Bakiye</th></tr></thead>
                <tbody>
                  {withBalance.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-8">Hareket bulunamadı</td></tr>}
                  {withBalance.map((m, i) => {
                    const isMovOpen = expandedMovKey === i;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          className={`cursor-pointer hover:bg-white/4 transition-colors select-none ${m.sourceType === "payment" && m.sourceObj?.isLegacy ? "opacity-75" : ""}`}
                          style={isMovOpen ? { background: "rgba(6,182,212,0.04)" } : {}}
                          onClick={() => setExpandedMovKey(isMovOpen ? null : i)}
                        >
                          <td className="whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <ChevronRight className={`w-3 h-3 shrink-0 text-slate-600 transition-transform ${isMovOpen ? "rotate-90" : ""}`} />
                              {fmtDate(m.date)}
                            </span>
                          </td>
                          <td><span className={`zen-chip !text-[10px] ${m.debit ? "bg-rose-500/10 text-rose-400" : "bg-emerald-500/10 text-emerald-400"}`}>{m.type}</span></td>
                          <td className="zen-mono">{m.ref}</td>
                          <td className="max-w-xs truncate text-slate-400">{m.note}</td>
                          <td className="text-right zen-mono">{m.debit ? fmt(m.dInCur, c.currency) : ""}</td>
                          <td className="text-right zen-mono">{m.credit ? fmt(m.cInCur, c.currency) : ""}</td>
                          <td className={`text-right zen-mono font-bold ${m.balance > 0 ? "text-rose-400" : m.balance < 0 ? "text-emerald-400" : ""}`}>{fmt(m.balance, c.currency)}</td>
                        </tr>
                        {isMovOpen && m.sourceObj && (
                          <tr>
                            <td colSpan={7} className="bg-[#060c18] px-5 py-3 border-b border-white/5">
                              {m.sourceType === "order" && (
                                <div className="space-y-1.5">
                                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Sipariş Kalemleri ({(m.sourceObj.items || []).length})</div>
                                  {(m.sourceObj.items || []).map((item, idx) => (
                                    <div key={idx} className="px-3 py-2.5 rounded-lg bg-white/3 border border-white/5">
                                      <div className="flex items-start justify-between gap-2 mb-1.5">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="font-semibold text-xs text-slate-200">{item.product || "—"}</span>
                                          {item.zipModeli && <span className="text-[9px] px-1 py-0.5 rounded bg-sky-500/15 text-sky-400 border border-sky-500/20">{item.zipModeli}</span>}
                                          {item.cableDir && <span className="text-[9px] px-1 py-0.5 rounded bg-white/5 text-slate-400 border border-white/10">{item.cableDir}</span>}
                                        </div>
                                        <span className="font-bold zen-mono text-xs text-slate-200 shrink-0">{fmt((item.unitPrice || 0) * (item.qty || 1), item.currency || m.sourceObj.currency || "TL")}</span>
                                      </div>
                                      <div className="flex flex-wrap gap-1">
                                        {item.widthCm > 0 && item.heightCm > 0 && (
                                          <span className="px-1.5 py-0.5 rounded bg-[#1a2a4a] border border-[#2a4a7a] text-sky-300 text-[10px] font-bold">{item.widthCm} × {item.heightCm} cm</span>
                                        )}
                                        {item.fabricCode && <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300 text-[10px] font-mono">{item.fabricCode}</span>}
                                        {item.ralCode && <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400 text-[10px]">{item.ralCode}</span>}
                                        {item.motorLabel && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px]">{item.motorLabel}</span>}
                                      </div>
                                    </div>
                                  ))}
                                  {(m.sourceObj.items || []).length === 0 && <span className="text-xs text-slate-500 italic">Kalem bilgisi yok</span>}
                                  {m.sourceObj.notes && <div className="text-xs text-slate-500 mt-1">Not: {m.sourceObj.notes}</div>}
                                </div>
                              )}
                              {m.sourceType === "invoice" && (
                                <div className="space-y-1.5">
                                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Fatura Kalemleri ({(m.sourceObj.items || []).length})</div>
                                  {(m.sourceObj.items || []).map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/3 border border-white/5 text-xs">
                                      <span className="font-semibold text-slate-200">{item.name || item.product || item.description || "—"}</span>
                                      <span className="font-bold zen-mono text-slate-200 shrink-0">{fmt((item.unitPrice || item.price || 0) * (item.qty || item.quantity || 1), m.sourceObj.currency || "TL")}</span>
                                    </div>
                                  ))}
                                  {(m.sourceObj.items || []).length === 0 && <span className="text-xs text-slate-500 italic">Kalem bilgisi yok</span>}
                                </div>
                              )}
                              {m.sourceType === "payment" && (
                                <div className="flex flex-wrap gap-4 text-xs text-slate-400 items-center">
                                  <span>Tür: <span className="text-slate-200">{m.sourceObj.kind || "tahsilat"}</span></span>
                                  <span>Yöntem: <span className="text-slate-200">{({ havale: "Havale", nakit: "Nakit", banka: "Banka", kart: "Kart", cek: "Çek" })[m.sourceObj.method] || m.sourceObj.method || "—"}</span></span>
                                  {m.sourceObj.bankName && <span>Banka: <span className="text-slate-200">{m.sourceObj.bankName}</span></span>}
                                  {m.sourceObj.note && <span>Not: <span className="text-slate-200">{m.sourceObj.note}</span></span>}
                                  <span className="font-bold text-sm text-white">{fmt(m.sourceObj.amount || 0, m.sourceObj.currency || "TL")}</span>
                                  {/* Sipariş bağlantısı */}
                                  {m.sourceObj.orderId && (
                                    <button
                                      data-testid={`goto-order-${m.sourceObj.orderId}`}
                                      onClick={() => { setViewingOrderId(m.sourceObj.orderId); setActiveView("satis"); setExpandedOrderId(m.sourceObj.orderId); }}
                                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-white/8 hover:bg-violet-500/20 text-violet-400 border border-white/10 transition-colors"
                                    ><ExternalLink className="w-3 h-3" /> #{m.sourceObj.orderId}</button>
                                  )}
                                  {!m.sourceObj.isLegacy && isAdmin && (
                                    <div className="ml-auto flex gap-1.5">
                                      <button
                                        data-testid={`hareket-edit-${m.sourceObj.id}`}
                                        onClick={() => setEditingPayment(m.sourceObj)}
                                        className="flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-white/8 hover:bg-cyan-500/20 text-cyan-400 border border-white/10 transition-colors"
                                      ><Pencil className="w-3 h-3" /> Düzenle</button>
                                      <button
                                        data-testid={`hareket-sil-${m.sourceObj.id}`}
                                        onClick={() => deletePaymentFromStatement(m.sourceObj)}
                                        className="flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-white/8 hover:bg-rose-500/20 text-rose-400 border border-white/10 transition-colors"
                                      ><Trash2 className="w-3 h-3" /> Sil</button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Toplam Bakiye — tek ve net */}
                  <tr className="bg-white/5 border-t-2 border-white/10">
                    <td colSpan={5} className="text-right font-bold text-sm text-slate-300 px-4 py-3">
                      Toplam Bakiye
                      <div className={`text-[10px] font-normal mt-0.5 ${(c.balance || 0) > 0 ? "text-rose-400/70" : (c.balance || 0) < 0 ? "text-emerald-400/70" : "text-slate-400/70"}`}>
                        {(c.balance || 0) > 0 ? "Müşteri size borçlu" : (c.balance || 0) < 0 ? "Müşteriden alacaklısınız (fazla ödeme)" : "Hesap kapalı — ne borç ne alacak"}
                      </div>
                    </td>
                    <td colSpan={2} className={`text-right zen-mono font-black text-lg px-4 py-3 ${(c.balance || 0) > 0 ? "text-rose-400" : "text-emerald-400"}`}>
                      {fmt(c.balance || 0, c.currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeView === "satis" && (
          <div className="zen-panel !p-0 overflow-hidden">
            {salesOrders.length === 0 && <div className="py-12 text-center text-slate-500 text-sm">Satış siparişi bulunamadı</div>}
            {salesOrders.map(o => {
              const isOpen = expandedOrderId === o.id;
              const scol = STATUS_COLOR[o.status] || "slate";
              return (
                <div key={o.id}>
                  {/* Satır başlığı — tıklanabilir */}
                  <div
                    data-testid={`cfp-order-${o.id}`}
                    onClick={() => setExpandedOrderId(isOpen ? null : o.id)}
                    className="flex items-center gap-4 px-5 py-3.5 border-b border-white/5 hover:bg-white/3 transition-colors cursor-pointer select-none"
                    style={isOpen ? { background: "rgba(6,182,212,0.05)", borderColor: "rgba(6,182,212,0.15)" } : {}}
                  >
                    <ChevronRight className={`w-4 h-4 shrink-0 text-slate-600 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-xs font-bold text-sky-400 zen-mono">{ono(o)}</span>
                        <span className={`zen-chip !text-[10px] bg-${scol}-500/10 text-${scol}-400`}>{STATUS_LABELS[o.status]?.tr || o.status}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {fmtDate(o.createdAt)} · {o.projectName && <span className="text-slate-400 font-semibold">{o.projectName} · </span>}{(o.items || [])[0]?.product || "—"}{o.items?.length > 1 ? ` +${o.items.length - 1} kalem` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold zen-mono text-slate-200">{fmt(o.total || 0, o.currency || "TL")}</div>
                      {o.currency !== c.currency && <div className="text-[10px] text-slate-600">{fmt(convertPrice(o.total || 0, o.currency, c.currency, rates), c.currency)}</div>}
                    </div>
                  </div>

                  {/* Genişletilmiş detay */}
                  {isOpen && (
                    <div className="border-b border-white/5 bg-[#060c18] px-5 py-4">
                      {/* Kalemler */}
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Sipariş Kalemleri ({(o.items || []).length})</div>
                      <div className="space-y-2 mb-4">
                        {(o.items || []).map((item, idx) => (
                          <div key={idx} className="px-3.5 py-3 rounded-xl bg-white/3 border border-white/5">
                            {/* Başlık satırı */}
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-sm text-slate-100">{item.product || "—"}</span>
                                {item.zipModeli && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 font-semibold border border-sky-500/20">{item.zipModeli}</span>}
                                {item.cableDir && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-400 border border-slate-500/20">{item.cableDir}</span>}
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-bold zen-mono text-slate-100">{fmt((item.unitPrice || 0) * (item.qty || 1), item.currency || o.currency || "TL")}</div>
                                {(item.discountPercent > 0) && <div className="text-[10px] text-emerald-400">-%{item.discountPercent} iskonto</div>}
                                <div className="text-[10px] text-slate-600">{item.qty || 1} {item.unit || "adet"}</div>
                              </div>
                            </div>
                            {/* Detay chip'leri */}
                            <div className="flex flex-wrap gap-1.5">
                              {item.widthCm > 0 && item.heightCm > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#1a2a4a] border border-[#2a4a7a] text-sky-300 text-xs font-bold tracking-tight">
                                  {item.widthCm} × {item.heightCm} cm
                                </span>
                              )}
                              {item.fabricCode && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-slate-300 text-[11px] font-mono">
                                  {item.fabricCode}
                                </span>
                              )}
                              {item.ralCode && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-slate-400 text-[11px]">
                                  {item.ralCode}{item.boyaTuru ? ` ${item.boyaTuru}` : ""}
                                </span>
                              )}
                              {item.motorLabel && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px]">
                                  {item.motorLabel}
                                </span>
                              )}
                              {item.extrasLabel && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-500/10 border border-violet-500/20 text-violet-400 text-[11px]">
                                  {item.extrasLabel}
                                </span>
                              )}
                              {item.roomName && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-slate-400 text-[11px]">
                                  {item.roomName}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Özet satır */}
                      <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-white/5 text-xs text-slate-500">
                        <span>Tarih: <span className="text-slate-300">{fmtDate(o.createdAt)}</span></span>
                        {o.approvedAt && <span>Onay: <span className="text-slate-300">{fmtDate(o.approvedAt)}</span></span>}
                        {o.projectName && <span>Proje: <span className="text-slate-300 font-semibold">{o.projectName}</span></span>}
                        {o.discountPercent > 0 && <span className="text-emerald-400 font-semibold">%{o.discountPercent} iskonto uygulandı</span>}
                        {o.vatExempt && <span className="text-sky-400">KDV Muaf</span>}
                        {o.notes && <div className="w-full text-slate-500 text-[11px] mt-1 leading-relaxed border-t border-white/5 pt-2">Not: {o.notes}</div>}
                        <span className="ml-auto font-bold text-base text-white">{fmt(o.total || 0, o.currency || "TL")}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ────── ALIŞ SİPARİŞLERİ ────────────────────────────────────── */}
        {activeView === "alis" && (
          <div className="zen-panel !p-0 overflow-hidden">
            {purchaseInvoices.length === 0 && <div className="py-12 text-center text-slate-500 text-sm">Alış faturası / siparişi bulunamadı</div>}
            {purchaseInvoices.map(inv => {
              const isOpen = expandedInvoiceId === inv.id;
              const typeLbl = { satis: "Satış", alis: "Alış", satis_iade: "Satış İade", alis_iade: "Alış İade", irsaliye: "İrsaliye" }[inv.type] || "Fatura";
              return (
                <div key={inv.id}>
                  {/* Satır başlığı — tıklanabilir */}
                  <div
                    data-testid={`cfp-invoice-${inv.id}`}
                    onClick={() => setExpandedInvoiceId(isOpen ? null : inv.id)}
                    className="flex items-center gap-4 px-5 py-3.5 border-b border-white/5 hover:bg-white/3 transition-colors cursor-pointer select-none"
                    style={isOpen ? { background: "rgba(139,92,246,0.05)", borderColor: "rgba(139,92,246,0.15)" } : {}}
                  >
                    <ChevronRight className={`w-4 h-4 shrink-0 text-slate-600 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-violet-400 zen-mono">{inv.docNo || inv.id}</span>
                        <span className="zen-chip !text-[10px] bg-violet-500/10 text-violet-400">{typeLbl}</span>
                      </div>
                      <div className="text-[11px] text-slate-500">{fmtDate(inv.issueDate)} · {(inv.items || []).length} kalem</div>
                    </div>
                    <div className="text-sm font-bold zen-mono text-slate-200">{fmt(inv.total || 0, inv.currency || "TL")}</div>
                  </div>

                  {/* Genişletilmiş fatura detayı */}
                  {isOpen && (
                    <div className="border-b border-white/5 bg-[#060c18] px-5 py-4">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Fatura Kalemleri ({(inv.items || []).length})</div>
                      <div className="space-y-1.5 mb-4">
                        {(inv.items || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white/3 border border-white/5 text-xs">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-200">{item.name || item.product || item.description || "—"}</div>
                              {(item.description && item.description !== item.name) && (
                                <div className="text-[11px] text-slate-500 mt-0.5">{item.description}</div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-slate-400 text-[11px]">{item.qty || item.quantity || 1} {item.unit || "adet"}</div>
                              <div className="font-bold zen-mono text-slate-200">{fmt((item.unitPrice || item.price || 0) * (item.qty || item.quantity || 1), inv.currency || "TL")}</div>
                            </div>
                          </div>
                        ))}
                        {(inv.items || []).length === 0 && (
                          <div className="text-xs text-slate-500 italic px-1">Kalem bilgisi bulunamadı</div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-white/5 text-xs text-slate-500">
                        <span>Fatura Tarihi: <span className="text-slate-300">{fmtDate(inv.issueDate)}</span></span>
                        {inv.dueDate && <span>Vade: <span className="text-slate-300">{fmtDate(inv.dueDate)}</span></span>}
                        {inv.notes && <span>Not: <span className="text-slate-300">{inv.notes}</span></span>}
                        <span className="ml-auto font-bold text-base text-white">{fmt(inv.total || 0, inv.currency || "TL")}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ────── FİYATLANDIRMA ────────────────────────────────────── */}
        {activeView === "fiyatlandirma" && (
          <CustomerPricingTab customer={c} onSave={(updated) => setC({ ...c, ...updated })} />
        )}

      </div>

      {/* ── MODALLER ──────────────────────────────────────────────────── */}
      {showEdit && (
        <CustomerForm data={c} onClose={() => setShowEdit(false)} onSave={async (f) => {
          try {
            if (c.id) await api.put(`/customers/${c.id}`, f);
            setC({ ...c, ...f });
            setShowEdit(false);
            toast.success("Müşteri güncellendi");
            setRefresh(r => r + 1);
          } catch (e) { toast.error("Kayıt hatası"); }
        }} />
      )}
      {showMove && (
        <MovementForm customer={c} orders={orders.filter(o => o.customerId === c.id)} onClose={() => setShowMove(false)} onSaved={() => { setShowMove(false); setRefresh(r => r + 1); }} />
      )}
      {showShare && (
        <StatementShareModal customer={c} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

export function StatementDialog({ customer, onClose }) {
  // Geriye dönük uyumluluk — CustomerFullPage'e yönlendir
  return <CustomerFullPage customer={customer} onClose={onClose} />;
}

// Yeni hareket ekleme formu — nakit/havale/banka/çek/kart/tahakkuk
function MovementForm({ customer, orders, onClose, onSaved }) {
  const { tr, lang } = useContext(UICtx);
  const [f, setF] = useState({
    kind: "tahsilat", method: "havale", amount: 0, currency: customer.currency || "TL",
    date: new Date().toISOString().slice(0, 10), orderId: "", note: "",
    checkNo: "", checkDueDate: "", checkBank: "",
  });

  const KIND_OPTIONS = [
    { id: "tahsilat", label: "Tahsilat", desc: "Müşteriden para ALDIK — bakiye AZALIR (borcu düşer)", color: "emerald" },
    { id: "tediye", label: "Ödeme (Tediye)", desc: "Müşteriye para VERDİK — bakiye ARTAR (borcu yükselir)", color: "sky" },
    { id: "alacak_tahakkuku", label: "Alacak Tahakkuku", desc: "Elden para YOK, belge kaydı — bakiye AZALIR (borcu düşer)", color: "rose" },
    { id: "borc_tahakkuku", label: "Borç Tahakkuku", desc: "Elden para YOK, belge kaydı (fatura/sipariş) — bakiye ARTAR", color: "amber" },
  ];
  const METHOD_OPTIONS = [
    { id: "nakit", label: "Nakit" },
    { id: "havale", label: "Havale / EFT" },
    { id: "banka", label: "Banka" },
    { id: "kart", label: "Kredi Kartı" },
    { id: "cek", label: "Çek" },
  ];

  const submit = async () => {
    if (!f.amount || f.amount <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    try {
      await api.post("/payments", { ...f, customerId: customer.id });
      toast.success("Hareket kaydedildi, cari bakiye güncellendi");
      onSaved();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-2xl p-5 zen-anim-up max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold">Yeni Cari Hareket — {customer.name}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {/* Kind selection */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {KIND_OPTIONS.map(k => {
            const active = f.kind === k.id;
            return (
              <button
                key={k.id} type="button" data-testid={`mv-kind-${k.id}`}
                onClick={() => setF({ ...f, kind: k.id })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  active
                    ? `border-${k.color}-500 bg-${k.color}-500/10`
                    : "border-slate-200 dark:border-[#22304A] hover:border-slate-300"
                }`}
              >
                <div className={`text-xs font-bold ${active ? `text-${k.color}-600 dark:text-${k.color}-400` : "text-slate-600 dark:text-slate-300"}`}>{k.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{k.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Method (only for tahsilat/tediye, not for tahakkuk) */}
        {(f.kind === "tahsilat" || f.kind === "tediye") && (
          <div className="mb-4">
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-2">{tr("Ödeme Yöntemi")}</label>
            <div className="flex gap-2 flex-wrap">
              {METHOD_OPTIONS.map(m => (
                <button key={m.id} type="button" onClick={() => setF({ ...f, method: m.id })}
                  className={`px-3 py-2 rounded-md text-xs font-semibold border ${
                    f.method === m.id
                      ? "border-[#E5322A] bg-[#E5322A] text-white"
                      : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300"
                  }`}
                >{m.label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Form fields */}
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Tutar")}</label>
            <input type="number" step="0.01" className="zen-input !text-right !font-bold" data-testid="mv-amount" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Para Birimi")}</label>
            <select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
              <option>TL</option><option>EUR</option><option>USD</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Tarih")}</label>
            <input type="date" className="zen-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>

          {(f.kind === "tahsilat" || f.kind === "tediye") && (
            <div className="md:col-span-3">
              <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Bağlı Sipariş (opsiyonel)")}</label>
              <select className="zen-input" value={f.orderId} onChange={(e) => setF({ ...f, orderId: e.target.value })}>
                <option value="">{tr("-- yok --")}</option>
                {orders.filter(o => o.status !== "teklif").map(o => (
                  <option key={o.id} value={o.id}>{ono(o)} · {fmt(o.total, o.currency)} · {(o.paymentStatus || "odenmedi")}</option>
                ))}
              </select>
            </div>
          )}

          {f.method === "cek" && (f.kind === "tahsilat" || f.kind === "tediye") && (
            <>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Çek No")}</label>
                <input className="zen-input" value={f.checkNo} onChange={(e) => setF({ ...f, checkNo: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Vade Tarihi")}</label>
                <input type="date" className="zen-input" value={f.checkDueDate} onChange={(e) => setF({ ...f, checkDueDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Banka")}</label>
                <input className="zen-input" value={f.checkBank} onChange={(e) => setF({ ...f, checkBank: e.target.value })} />
              </div>
            </>
          )}

          <div className="md:col-span-3">
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">{tr("Açıklama / Not")}</label>
            <input className="zen-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="zen-btn-ghost">{tr("İptal")}</button>
          <button onClick={submit} data-testid="mv-save" className="zen-btn-primary"><CheckCircle2 className="w-4 h-4" />{tr("Kaydet")}</button>
        </div>
      </div>
    </div>
  );
}

// ── AI Destekli Toplu Müşteri İçe Aktarma Modalı ──────────────────────────────
const IMPORT_TARGET_FIELDS = [
  { value: "ignore",          label: "— Atla —" },
  { value: "name",            label: "Ad / Firma Adı (zorunlu)" },
  { value: "phone",           label: "Telefon" },
  { value: "email",           label: "E-posta" },
  { value: "address",         label: "Adres" },
  { value: "currency",        label: "Para Birimi (TL/EUR/USD)" },
  { value: "type",            label: "Cari Tipi" },
  { value: "group",           label: "Cari Grubu" },
  { value: "taxNo",           label: "Vergi No" },
  { value: "discountPercent", label: "İskonto %" },
  { value: "notes",           label: "Not" },
];

function BulkImportModal({ onClose, onDone }) {
  const { tr } = useContext(UICtx);
  const [step, setStep] = useState(1); // 1=upload 2=mapping 3=preview 4=done
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [analyzed, setAnalyzed] = useState(null); // {headers,sampleRows,allRows,mapping,confidence,aiNotes,totalRows,duplicates}
  const [mapping, setMapping] = useState({});
  const [selected, setSelected] = useState([]); // boolean[] for each row
  const [duplicateMode, setDuplicateMode] = useState("skip"); // "skip" | "overwrite"
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const CONF_COLORS = { high: "text-emerald-500", medium: "text-amber-500", low: "text-rose-500" };
  const CONF_LABELS = { high: "Yüksek", medium: "Orta", low: "Düşük" };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/customers/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalyzed(data);
      setMapping(data.mapping || {});
      setSelected(data.allRows.map(() => true));
      setStep(2);
    } catch (e) {
      toast.error("Dosya analiz edilemedi: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const execImport = async () => {
    const approvedRows = analyzed.allRows.filter((_, i) => selected[i]);
    if (!approvedRows.length) { toast.error("En az 1 satır seçilmeli"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/import/customers/execute", {
        headers: analyzed.headers,
        mapping,
        rows: approvedRows,
        duplicateMode,
      });
      setResult(data);
      setStep(4);
    } catch (e) {
      toast.error("İçe aktarma hatası: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Mapped value for a cell
  const getMappedValue = (row, header) => {
    const field = mapping[header];
    if (!field || field === "ignore") return null;
    const idx = analyzed.headers.indexOf(header);
    return { field, value: String(row[idx] ?? "").trim() };
  };

  // Build preview row object
  const buildPreview = (row) => {
    const obj = {};
    analyzed.headers.forEach((h, i) => {
      const f = mapping[h];
      if (f && f !== "ignore" && row[i]) obj[f] = String(row[i]).trim();
    });
    return obj;
  };

  const selectedCount = selected.filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="zen-panel w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col zen-anim-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-[#22304A] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#E5322A]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#E5322A]" />
            </div>
            <div>
              <h3 className="font-bold text-lg">AI Destekli Toplu İçe Aktarma</h3>
              <p className="text-xs text-slate-500">Excel / CSV → Müşteri Listesi</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded-lg transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex px-6 py-3 gap-1 shrink-0 border-b border-slate-100 dark:border-[#1A2540]">
          {[
            { n: 1, label: "Dosya Yükle" },
            { n: 2, label: "Sütun Eşleştirme" },
            { n: 3, label: "Satır Önizleme" },
            { n: 4, label: "Sonuç" },
          ].map(({ n, label }) => (
            <div key={n} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${step === n ? "bg-[#E5322A] text-white" : step > n ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "text-slate-400"}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === n ? "bg-white/25" : step > n ? "bg-emerald-500/25" : "bg-slate-200 dark:bg-[#22304A]"}`}>{step > n ? "✓" : n}</span>
              {label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* STEP 1 — Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div
                data-testid="import-drop-zone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-12 flex flex-col items-center gap-4 cursor-pointer transition-all select-none ${dragOver ? "border-[#E5322A] bg-[#E5322A]/5" : "border-slate-300 dark:border-[#22304A] hover:border-[#E5322A]/50 hover:bg-slate-50 dark:hover:bg-[#0C1220]"}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#E5322A]/10 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#E5322A]" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-slate-700 dark:text-slate-200">Excel veya CSV dosyası sürükleyin</p>
                  <p className="text-sm text-slate-500 mt-1">veya tıklayarak dosya seçin</p>
                  <p className="text-xs text-slate-400 mt-2">.xlsx · .xls · .csv · maks. 5 MB</p>
                </div>
                {loading && <div className="flex items-center gap-2 text-sm text-[#E5322A]"><div className="w-4 h-4 border-2 border-[#E5322A] border-t-transparent rounded-full animate-spin" />AI analiz ediyor...</div>}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="import-file-input"
                onChange={(e) => handleFile(e.target.files[0])} />
              <div className="zen-panel !p-3 bg-sky-500/5 border-sky-400/20 text-xs text-sky-700 dark:text-sky-300 space-y-1">
                <div className="font-semibold flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> AI Nasıl Çalışır?</div>
                <div>• Farklı programlardan export edilen dosyaları tanır (Bayiloji, Excel, Google Sheets, ERP...)</div>
                <div>• Sütun başlıklarını ve örnek verileri analiz ederek otomatik eşleştirir</div>
                <div>• AI yoksa kural tabanlı eşleştirme devreye girer — her zaman çalışır</div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl border border-emerald-400/30 bg-emerald-500/5">
                <div className="text-xs text-emerald-700 dark:text-emerald-300">
                  <span className="font-semibold">Sütun adlarından emin değilseniz</span> — ZEN ERP şablonunu indirip doldurun
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/customers/template`}
                  download="musteri_sablonu.xlsx"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors shrink-0"
                  data-testid="customer-template-download-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  Boş Şablon İndir
                </a>
              </div>
            </div>
          )}

          {/* STEP 2 — Mapping */}
          {step === 2 && analyzed && (
            <div className="space-y-4">
              {/* AI confidence banner */}
              <div className={`zen-panel !p-3 flex items-start gap-3 ${analyzed.confidence === "high" ? "bg-emerald-500/5 border-emerald-400/20" : analyzed.confidence === "medium" ? "bg-amber-500/5 border-amber-400/20" : "bg-rose-500/5 border-rose-400/20"}`}>
                <Zap className={`w-4 h-4 mt-0.5 ${CONF_COLORS[analyzed.confidence]}`} />
                <div>
                  <div className={`text-xs font-bold ${CONF_COLORS[analyzed.confidence]}`}>
                    {analyzed.confidence === "low" ? "AI Hatası" : analyzed.confidence === "medium" ? "Kural Tabanlı Eşleştirme" : "AI Güven Skoru: Yüksek"}
                  </div>
                  {analyzed.aiNotes && <div className="text-xs text-slate-500 mt-1">{analyzed.aiNotes}</div>}
                  <div className="text-xs text-slate-400 mt-0.5">"{fileName}" · {analyzed.totalRows} satır bulundu</div>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-300">AI'nın önerdiği eşleştirmeyi kontrol edin ve gerekirse düzeltin:</p>

              <div className="overflow-x-auto">
                <table className="zen-table text-sm">
                  <thead><tr>
                    <th>Kaynak Sütun</th>
                    <th>Örnek Veri</th>
                    <th>Hedef Alan</th>
                  </tr></thead>
                  <tbody>
                    {analyzed.headers.map((h, i) => {
                      const sample = analyzed.sampleRows.map(r => String(r[i] ?? "").trim()).filter(Boolean).slice(0, 3).join(", ");
                      const field = mapping[h] || "ignore";
                      return (
                        <tr key={h} data-testid={`import-map-row-${i}`}>
                          <td className="font-mono font-semibold text-[#E5322A]">{h}</td>
                          <td className="text-xs text-slate-500 max-w-[200px] truncate">{sample || "—"}</td>
                          <td>
                            <select
                              data-testid={`import-map-select-${i}`}
                              className="zen-input !py-1.5 text-xs"
                              value={field}
                              onChange={(e) => setMapping(m => ({ ...m, [h]: e.target.value }))}
                            >
                              {IMPORT_TARGET_FIELDS.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3 — Row Preview */}
          {step === 3 && analyzed && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-bold text-[#E5322A]">{selectedCount}</span> / {analyzed.allRows.length} satır seçildi
                  {Object.keys(analyzed.duplicates || {}).length > 0 && (
                    <span className="ml-2 zen-chip bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px]">
                      ⚠ {Object.keys(analyzed.duplicates).length} kopya
                    </span>
                  )}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  {/* Kopya modu seçimi */}
                  {Object.keys(analyzed.duplicates || {}).length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className="text-amber-600 font-semibold">Kopyalar:</span>
                      <select
                        data-testid="import-dup-mode"
                        className="zen-input !py-1 !text-xs"
                        value={duplicateMode}
                        onChange={e => setDuplicateMode(e.target.value)}
                      >
                        <option value="skip">Atla (değiştirme)</option>
                        <option value="overwrite">Üzerine Yaz (güncelle)</option>
                      </select>
                    </div>
                  )}
                  <button onClick={() => setSelected(analyzed.allRows.map(() => true))} className="zen-btn-ghost !text-xs !py-1.5">Tümünü Seç</button>
                  <button onClick={() => setSelected(analyzed.allRows.map(() => false))} className="zen-btn-ghost !text-xs !py-1.5">Tümünü Kaldır</button>
                </div>
              </div>

              <div className="overflow-auto max-h-[50vh] rounded-xl border border-slate-200 dark:border-[#22304A]">
                <table className="zen-table text-xs">
                  <thead className="sticky top-0 bg-white dark:bg-[#0C1220]">
                    <tr>
                      <th className="w-10">
                        <input type="checkbox" className="accent-[#E5322A]"
                          checked={selected.every(Boolean)}
                          onChange={(e) => setSelected(analyzed.allRows.map(() => e.target.checked))} />
                      </th>
                      <th>#</th>
                      {["Ad / Firma", "Telefon", "E-posta", "Adres", "Para B.", "Vergi No", "Tip"].map(l => <th key={l}>{l}</th>)}
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyzed.allRows.map((row, i) => {
                      const p = buildPreview(row);
                      const valid = !!p.name;
                      const isDup = !!(analyzed.duplicates || {})[i];
                      const dupInfo = (analyzed.duplicates || {})[i];
                      return (
                        <tr key={i} data-testid={`import-row-${i}`} className={`${!selected[i] ? "opacity-40" : ""} ${!valid ? "bg-rose-500/5" : isDup ? "bg-amber-500/5" : ""}`}>
                          <td><input type="checkbox" className="accent-[#E5322A]" checked={!!selected[i]}
                            onChange={(e) => setSelected(s => s.map((v, j) => j === i ? e.target.checked : v))} /></td>
                          <td className="text-slate-400">{i + 1}</td>
                          <td className="font-semibold">{p.name || <span className="text-rose-400">Boş!</span>}</td>
                          <td>{p.phone || "—"}</td>
                          <td>{p.email || "—"}</td>
                          <td className="max-w-[120px] truncate">{p.address || "—"}</td>
                          <td><span className={`zen-chip text-[10px] ${p.currency === "EUR" ? "bg-emerald-500/10 text-emerald-500" : p.currency === "USD" ? "bg-sky-500/10 text-sky-500" : "bg-slate-500/10 text-slate-500"}`}>{p.currency || "TL"}</span></td>
                          <td>{p.taxNo || "—"}</td>
                          <td>{p.type || "—"}</td>
                          <td>
                            {!valid ? (
                              <span className="text-rose-400 font-semibold">Ad boş</span>
                            ) : isDup ? (
                              <span title={`Eşleşen: ${dupInfo?.name}`} className="zen-chip bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] cursor-help">
                                ⚠ Kopya ({dupInfo?.matchField === "phone" ? "Tel" : "Email"})
                              </span>
                            ) : (
                              <span className="text-emerald-500 font-semibold">✓ Yeni</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 4 — Result */}
          {step === 4 && result && (
            <div className="space-y-4 text-center py-8">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <Check className="w-10 h-10 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-500">{result.imported} Müşteri İçe Aktarıldı</div>
                {result.errors?.length > 0 && (
                  <div className="text-sm text-rose-500 mt-1">{result.errors.length} satır atlandı</div>
                )}
              </div>
              {result.errors?.length > 0 && (
                <div className="zen-panel !p-3 max-h-40 overflow-y-auto text-left">
                  <div className="text-xs font-bold text-rose-500 mb-2">Atlanan Satırlar:</div>
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-xs text-slate-500">Satır {e.row}: {e.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer actions */}
        <div className="shrink-0 flex justify-between items-center px-6 py-4 border-t border-slate-200 dark:border-[#22304A]">
          <button onClick={onClose} className="zen-btn-ghost">{step === 4 ? "Kapat" : "İptal"}</button>
          <div className="flex gap-2">
            {step === 2 && (
              <button data-testid="import-go-preview" onClick={() => setStep(3)} className="zen-btn-primary">
                Satırları Önizle →
              </button>
            )}
            {step === 3 && (
              <>
                <button onClick={() => setStep(2)} className="zen-btn-ghost">← Geri</button>
                <button data-testid="import-execute-btn" onClick={execImport} disabled={loading || selectedCount === 0}
                  className="zen-btn-primary disabled:opacity-50">
                  {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Aktarılıyor...</> : <><Upload className="w-4 h-4" />{selectedCount} Müşteriyi Aktar</>}
                </button>
              </>
            )}
            {step === 4 && (
              <button data-testid="import-done-btn" onClick={() => { onDone(); onClose(); }} className="zen-btn-primary">
                <Check className="w-4 h-4" /> Tamam
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentEditModal({ payment, onClose, onSaved }) {
  const KIND_OPTS = [
    { v: "tahsilat",        l: "Tahsilat" },
    { v: "tediye",          l: "Tediye (Ödeme)" },
    { v: "alacak_tahakkuku",l: "Alacak Tahakkuku" },
    { v: "borc_tahakkuku",  l: "Borç Tahakkuku" },
  ];
  const METHOD_OPTS = [
    { v: "nakit",   l: "Nakit" },
    { v: "havale",  l: "Havale / EFT" },
    { v: "kart",    l: "Kredi Kartı" },
    { v: "cek",     l: "Çek" },
  ];
  const [f, setF] = useState({
    kind:     payment.kind    || "tahsilat",
    method:   payment.method  || "havale",
    amount:   payment.amount  || 0,
    currency: payment.currency|| "TL",
    date:     (payment.date   || "").slice(0, 10),
    note:     payment.note    || "",
    orderId:  payment.orderId || "",
  });
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!payment.customerId) return;
    api.get("/orders").then(({ data }) => {
      setOrders((data || []).filter(o => o.customerId === payment.customerId && o.status !== "teklif" && o.status !== "iptal"));
    }).catch(() => {});
  }, [payment.customerId]);

  const save = async () => {
    if (!f.amount || f.amount <= 0) return toast.error("Tutar giriniz");
    setSaving(true);
    try {
      const payload = { ...payment, ...f, amount: parseFloat(f.amount) };
      await api.put(`/payments/${payment.id}`, payload);
      toast.success("Hareket güncellendi");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const field = (label, key, type = "text", opts = null) => (
    <div className="space-y-1">
      <label className="text-xs text-slate-400">{label}</label>
      {opts ? (
        <select value={f[key]} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))}
          className="zen-input w-full text-sm">
          {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input type={type} value={f[key]} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))}
          className="zen-input w-full text-sm" />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0f1923] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="payment-edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="font-bold text-base">Hareketi Düzenle</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {field("Hareket Türü",  "kind",     "text", KIND_OPTS)}
          {field("Ödeme Yöntemi", "method",   "text", METHOD_OPTS)}
          <div className="grid grid-cols-2 gap-3">
            {field("Tutar",       "amount",   "number")}
            {field("Para Birimi", "currency", "text", [
              { v:"TL", l:"TL" }, { v:"EUR", l:"EUR" }, { v:"USD", l:"USD" }
            ])}
          </div>
          {field("Tarih",         "date",     "date")}
          {field("Açıklama / Not","note",     "text")}
          {/* Bağlı sipariş */}
          <div className="space-y-1">
            <label className="text-xs text-slate-400">Bağlı Sipariş</label>
            <select
              value={f.orderId}
              onChange={e => setF(p => ({ ...p, orderId: e.target.value }))}
              className="zen-input w-full text-sm"
              data-testid="payment-edit-order-select"
            >
              <option value="">— Bağlantısız —</option>
              {orders.map(o => (
                <option key={o.id} value={o.id}>
                  {o.orderNo || o.id} · {(o.total || 0).toFixed(0)} {o.currency} · {o.status}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2 p-5 pt-0">
          <button onClick={onClose} className="zen-btn-ghost flex-1 text-sm">İptal</button>
          <button onClick={save} disabled={saving} className="zen-btn-primary flex-1 text-sm" data-testid="payment-edit-save-btn">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerForm({ data, onClose, onSave }) {
  const { tr, lang } = useContext(UICtx);
  const [f, setF] = useState({
    name: "", type: "Yurt İçi Müşteri", group: "", phone: "", email: "", taxNo: "",
    address: "", currency: "TL", balance: 0, discountPercent: 0, productDiscounts: {}, priceRules: [], notes: "",
    dealerGroupId: "",
    pricingSegment: "son_kullanici",
    gender: "",
    vatExemptEligible: false,
    vatExemptNote: "3065 sayılı KDV Kanunu Madde 11/1-a kapsamında KDV'den istisnadır",
    ...data,
  });
  const [dealerGroups, setDealerGroups] = useState([]);
  const [dealerUser, setDealerUser] = useState(null);
  const [dealerAllowedGroups, setDealerAllowedGroups] = useState([]);
  const [allProductGroups, setAllProductGroups] = useState([]);
  const [savingFoyleri, setSavingFoyleri] = useState(false);

  useEffect(() => { api.get("/dealer-groups").then(({ data }) => setDealerGroups(data || [])).catch(() => {}); }, []);
  // Mevcut müşteri varsa, bağlı bayi kullanıcısını ve ürün gruplarını yükle
  useEffect(() => {
    if (!data?.id) return;
    Promise.all([
      api.get("/auth/users").catch(() => ({ data: [] })),
      api.get("/product-groups").catch(() => ({ data: [] })),
    ]).then(([usersResp, pgResp]) => {
      const linked = (usersResp.data || []).find(u => u.role === "dealer" && u.customerId === data.id);
      setDealerUser(linked || null);
      setDealerAllowedGroups(linked?.allowedProductGroups || []);
      setAllProductGroups((pgResp.data || []).filter(g => g.active !== false));
    });
  }, [data?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveFoyleri = async () => {
    if (!dealerUser) return;
    setSavingFoyleri(true);
    try {
      await api.put(`/auth/users/${dealerUser.id}`, { allowedProductGroups: dealerAllowedGroups });
      toast.success(`Bayi sipariş föyleri güncellendi (${dealerAllowedGroups.length} föy)`);
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSavingFoyleri(false); }
  };
  // Ürün grubu bazlı iskonto helper (legacy productDiscounts alanı için)
  const PRODUCT_TYPES = PRICE_RULE_PRODUCT_TYPES.map(pt => ({ key: pt.key, label: pt.sub ? `${pt.label} (${pt.sub})` : pt.label }));
  const setProductDiscount = (key, val) =>
    setF(prev => ({ ...prev, productDiscounts: { ...(prev.productDiscounts || {}), [key]: val === "" ? undefined : Number(val) } }));
  const TYPES = [
    { id: "Yurt İçi Müşteri", label: "Yurt İçi Müşteri", color: "sky", desc: "Türkiye'de satış yaptığımız firma/kişi" },
    { id: "Yurt Dışı Müşteri", label: "Yurt Dışı Müşteri", color: "emerald", desc: "Yurt dışı satış yaptığımız firma/kişi" },
    { id: "Tedarikçi", label: "Tedarikçi", color: "purple", desc: "Alım yaptığımız firma" },
    { id: "Çalışan", label: "Çalışanlar", color: "amber", desc: "Personel avans/cari takibi" },
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-2xl p-6 zen-anim-up max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold">{data?.id ? tr("Cari Düzenle") : tr("Yeni Cari Hesap")}</h3>
            {(f.priceRules || []).length > 0 && (
              <span className="zen-chip bg-[#E5322A]/10 text-[#E5322A] text-[10px] font-semibold">★ {f.priceRules.length} Özel Fiyat Kuralı</span>
            )}
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {/* Cari Tipi (tek seçim) */}
        <div className="mb-4">
          <label className="block text-xs font-semibold mb-2 uppercase tracking-wider text-slate-500">
            {tr("Cari Tipi")}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TYPES.map(c => {
              const active = f.type === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`cust-type-${c.id}`}
                  onClick={() => setF({ ...f, type: c.id })}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${
                    active
                      ? `border-${c.color}-500 bg-${c.color}-500/10`
                      : "border-slate-200 dark:border-[#22304A] hover:border-slate-300"
                  }`}
                >
                  <div className={`text-xs font-bold flex items-center gap-2 ${active ? `text-${c.color}-600 dark:text-${c.color}-400` : "text-slate-600 dark:text-slate-300"}`}>
                    <span className={`w-3 h-3 rounded ${active ? `bg-${c.color}-500` : "bg-slate-300 dark:bg-slate-600"}`} />
                    {tr(c.label)}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{tr(c.desc)}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div><label className="block text-xs font-semibold mb-1">{tr("Ad / Ünvan")}</label><input data-testid="cust-name" className="zen-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Ana Grup / Etiket")}</label><input className="zen-input" placeholder={tr("opsiyonel (örn: A-Class Otel Grubu)")} value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Telefon")}</label><input className="zen-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("E-posta")}</label><input className="zen-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Vergi No")}</label><input className="zen-input" value={f.taxNo} onChange={(e) => setF({ ...f, taxNo: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Para Birimi")}</label>
            <select data-testid="cust-currency" className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
              <option>TL</option><option>EUR</option><option>USD</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Cinsiyet</label>
            <div className="flex gap-2">
              {[{v:"erkek",l:"Erkek (Bey)"},{v:"kadın",l:"Kadın (Hanım)"},{v:"",l:"Belirtilmemiş"}].map(o => (
                <button key={o.v} type="button"
                  data-testid={`cust-gender-${o.v||"unknown"}`}
                  onClick={() => setF({...f, gender: o.v})}
                  className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${f.gender === o.v ? "border-[#E5322A] bg-[#E5322A]/10 text-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-slate-300"}`}
                >{o.l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Bayi Grubu</label>
            <select data-testid="cust-dealer-group" className="zen-input" value={f.dealerGroupId || ""} onChange={(e) => setF({ ...f, dealerGroupId: e.target.value || "" })}>
              <option value="">— Bayi Grubu Yok —</option>
              {dealerGroups.map(g => (
                <option key={g.id} value={g.id}>{g.name}{g.discountPct > 0 ? ` (Genel: %${g.discountPct})` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Fiyat Segmenti</label>
            <select data-testid="cust-pricing-segment" className="zen-input" value={f.pricingSegment || "son_kullanici"} onChange={(e) => setF({ ...f, pricingSegment: e.target.value })}>
              <option value="son_kullanici">Son Kullanıcı — maliyet × 2</option>
              <option value="bayi">Bayi — maliyet + %35</option>
            </select>
          </div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Cari İndirim (%)")}</label><input type="number" className="zen-input" value={f.discountPercent} onChange={(e) => setF({ ...f, discountPercent: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Açılış Bakiye")}</label><input type="number" className="zen-input" value={f.balance} onChange={(e) => setF({ ...f, balance: Number(e.target.value) })} /><p className="text-[10px] text-slate-400 mt-1">Devir bakiyesi: hareketlerden ÖNCEKİ borç. Genelde 0 bırakın; yalnız eski sistemden devir varsa girin.</p></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Adres")}</label><input className="zen-input" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Not")}</label><textarea rows={2} className="zen-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>

        {/* Muhasebe / KDV İstisnası */}
        <div className="mt-4 border-t border-slate-200 dark:border-[#22304A] pt-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{tr("İhracat / KDV İstisnası")}</label>
              <p className="text-[10px] text-slate-400 mt-0.5">Açılırsa, bu cari için teklif/sipariş/fatura ekranlarında "İstisna Uygula" seçeneği sunulur (otomatik uygulanmaz).</p>
            </div>
            <button
              type="button"
              data-testid="cust-vat-exempt-toggle"
              onClick={() => setF(p => ({ ...p, vatExemptEligible: !p.vatExemptEligible }))}
              className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${f.vatExemptEligible ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${f.vatExemptEligible ? "translate-x-5" : ""}`} />
            </button>
          </div>
          {f.vatExemptEligible && (
            <div className="mt-3">
              <label className="block text-xs font-semibold mb-1">{tr("İstisna Yasal Notu (belgeye eklenir)")}</label>
              <textarea
                data-testid="cust-vat-exempt-note"
                rows={2}
                className="zen-input"
                value={f.vatExemptNote}
                onChange={(e) => setF({ ...f, vatExemptNote: e.target.value })}
              />
            </div>
          )}
        </div>
        <div className="mt-4 border-t border-slate-200 dark:border-[#22304A] pt-4">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{tr("Ürün Grubu İskontoları (%)")}</label>
          <p className="text-[10px] text-slate-400 mb-3">{tr("Boş bırakılan gruplara 'Cari İndirim' uygulanır.")}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PRODUCT_TYPES.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-[10px] text-slate-500 mb-0.5">{label}</label>
                <input
                  type="number"
                  min="0" max="100"
                  className="zen-input !text-sm"
                  placeholder="—"
                  value={(f.productDiscounts || {})[key] ?? ""}
                  onChange={(e) => setProductDiscount(key, e.target.value)}
                  data-testid={`cust-disc-${key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── Müşteriye Özel Fiyat Kuralları ─────────────────────────────── */}
        <div className="mt-4 border-t border-slate-200 dark:border-[#22304A] pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{tr("Müşteriye Özel Fiyat Kuralları")}</label>
              <p className="text-[10px] text-slate-400 mt-0.5">Bayi grubunun üzerinde öncelik taşır. Sabit fiyat veya iskonto.</p>
            </div>
            <button
              data-testid="cust-add-price-rule"
              type="button"
              onClick={() => setF(p => ({ ...p, priceRules: [...(p.priceRules || []), { slug: "stor", fabricGroup: "", type: "fixed", val: 0, currency: "EUR" }] }))}
              className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[#E5322A]/10 text-[#E5322A] hover:bg-[#E5322A]/20 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Kural Ekle
            </button>
          </div>

          {(f.priceRules || []).length === 0 && (
            <div className="text-[11px] text-slate-400 italic py-2">Henüz özel fiyat kuralı yok.</div>
          )}

          <div className="space-y-2">
            {(f.priceRules || []).map((rule, i) => {
              const fabricOptions = PRICE_RULE_FABRIC_GROUPS[rule.slug] || [];
              const updateRule = (key, val) => setF(p => {
                const rules = [...(p.priceRules || [])];
                rules[i] = { ...rules[i], [key]: val };
                if (key === "slug") rules[i].fabricGroup = "";
                return { ...p, priceRules: rules };
              });
              return (
                <div key={i} data-testid={`cust-price-rule-${i}`} className="flex flex-wrap gap-2 items-end p-3 rounded-xl border border-slate-200 dark:border-[#22304A] bg-slate-50/50 dark:bg-[#0C1220]">
                  {/* Ürün Grubu */}
                  <div className="flex-1 min-w-[110px]">
                    <label className="text-[10px] text-slate-500 block mb-0.5">Ürün Grubu</label>
                    <select className="zen-input !py-1.5 !text-xs" value={rule.slug} onChange={e => updateRule("slug", e.target.value)} data-testid={`cust-rule-slug-${i}`}>
                      {PRICE_RULE_PRODUCT_TYPES.map(pt => <option key={pt.key} value={pt.key}>{pt.label}</option>)}
                    </select>
                  </div>
                  {/* Kumaş Grubu (opsiyonel) */}
                  <div className="flex-1 min-w-[110px]">
                    <label className="text-[10px] text-slate-500 block mb-0.5">Kumaş Grubu <span className="text-slate-400">(isteğe bağlı)</span></label>
                    <select className="zen-input !py-1.5 !text-xs" value={rule.fabricGroup || ""} onChange={e => updateRule("fabricGroup", e.target.value)} data-testid={`cust-rule-fg-${i}`}>
                      <option value="">— Tüm Kumaşlar —</option>
                      {fabricOptions.map(fg => <option key={fg.key} value={fg.key}>{fg.label}</option>)}
                    </select>
                  </div>
                  {/* Fiyat Türü */}
                  <div className="w-[110px]">
                    <label className="text-[10px] text-slate-500 block mb-0.5">Tür</label>
                    <select className="zen-input !py-1.5 !text-xs" value={rule.type} onChange={e => updateRule("type", e.target.value)} data-testid={`cust-rule-type-${i}`}>
                      <option value="fixed">Sabit Fiyat (m²)</option>
                      <option value="discount">İskonto (%)</option>
                    </select>
                  </div>
                  {/* Değer */}
                  <div className="w-[80px]">
                    <label className="text-[10px] text-slate-500 block mb-0.5">{rule.type === "fixed" ? "Fiyat" : "İskonto %"}</label>
                    <input type="number" min="0" step="0.01" className="zen-input !py-1.5 !text-xs" value={rule.val || ""} onChange={e => updateRule("val", Number(e.target.value))} data-testid={`cust-rule-val-${i}`} />
                  </div>
                  {/* Para Birimi (sadece fixed) */}
                  {rule.type === "fixed" && (
                    <div className="w-[70px]">
                      <label className="text-[10px] text-slate-500 block mb-0.5">Para B.</label>
                      <select className="zen-input !py-1.5 !text-xs" value={rule.currency || "EUR"} onChange={e => updateRule("currency", e.target.value)} data-testid={`cust-rule-cur-${i}`}>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="TL">TL</option>
                      </select>
                    </div>
                  )}
                  {/* Sil */}
                  <button type="button" onClick={() => setF(p => ({ ...p, priceRules: (p.priceRules || []).filter((_, idx) => idx !== i) }))} className="p-1.5 rounded-lg hover:bg-rose-500/10 text-rose-500 transition-colors self-end mb-0.5" data-testid={`cust-rule-del-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="zen-btn-ghost">{tr("İptal")}</button><button data-testid="cust-save" onClick={() => onSave(f)} className="zen-btn-primary">{tr("Kaydet")}</button></div>

        {/* ── Bayi Sipariş Föyleri ─────────────────────────────────────── */}
        {data?.id && (
          <div className="mt-4 border-t border-slate-200 dark:border-[#22304A] pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bayi Sipariş Föyleri</label>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {dealerUser
                    ? `Bu müşteriye bağlı bayi: ${dealerUser.name || dealerUser.username} — hangi sipariş formlarına erişebileceğini seçin.`
                    : "Bu müşteriye henüz bayi hesabı bağlı değil. Ayarlar → Kullanıcılar'dan oluşturun."}
                </p>
              </div>
              {dealerUser && (
                <button
                  data-testid="cust-save-foyleri"
                  type="button"
                  onClick={saveFoyleri}
                  disabled={savingFoyleri}
                  className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                >
                  {savingFoyleri ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Kaydet
                </button>
              )}
            </div>
            {dealerUser && allProductGroups.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {allProductGroups.map(g => {
                  const active = dealerAllowedGroups.includes(g.slug);
                  return (
                    <button
                      key={g.slug}
                      type="button"
                      data-testid={`cust-foyleri-${g.slug}`}
                      onClick={() => setDealerAllowedGroups(prev =>
                        active ? prev.filter(s => s !== g.slug) : [...prev, g.slug]
                      )}
                      className={`p-2.5 rounded-lg border-2 text-left transition-all text-xs font-semibold ${
                        active
                          ? "border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
                          : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-slate-300"
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full inline-block mr-1.5 ${active ? "bg-indigo-500" : "bg-slate-300"}`} />
                      {g.name}
                    </button>
                  );
                })}
              </div>
            )}
            {dealerUser && allProductGroups.length === 0 && (
              <div className="text-[11px] text-slate-400 italic">Ürün grubu bulunamadı.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ PRICES MODULE ============
function PricingModule({ readOnly = false }) {
  const { tr } = useContext(UICtx);
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState({ eurToTl: 38, usdToTl: 34 });
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = async () => {
    setRows((await api.get("/prices")).data);
    setRates((await api.get("/rates")).data);
  };
  useEffect(() => { load(); }, []);
  const filtered = rows.filter((r) => (r.name + r.code + r.catalog).toLowerCase().includes(q.toLowerCase()));
  const save = async (d) => {
    if (editing?.id) await api.put(`/prices/${editing.id}`, d); else await api.post("/prices", d);
    toast.success("Kaydedildi"); setEditing(null); load();
  };
  const del = async (p) => {
    if (!window.confirm(`${p.name} silinsin mi?`)) return;
    await api.delete(`/prices/${p.id}`); toast.success("Silindi"); load();
  };
  const saveRates = async () => {
    await api.put("/rates", { eurToTl: rates.eurToTl, usdToTl: rates.usdToTl });
    toast.success("Kurlar güncellendi");
  };
  const refreshTCMB = async () => {
    try {
      const { data } = await api.post("/rates/refresh-tcmb");
      if (data.ok) {
        toast.success(`TCMB kurları güncellendi: 1€ = ₺${data.eurToTl}`);
        load();
      } else {
        toast.error(`TCMB bağlantı hatası: ${data.error || "Bilinmeyen hata"}`);
      }
    } catch (e) { toast.error("Kurlar güncellenemedi"); }
  };
  return (
    <div className="space-y-4 zen-anim-up" data-testid="pricing-module">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight">{tr(readOnly ? "Fiyat Sorgulama" : "Fiyat Listeleri")}</h2>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 zen-panel px-3"><Search className="w-4 h-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Ara...")} className="bg-transparent outline-none text-sm py-2 w-56" data-testid="pricing-search-input" /></div>
          {!readOnly && <button data-testid="new-price-btn" onClick={() => setEditing({})} className="zen-btn-primary"><Plus className="w-4 h-4" />{tr("Yeni Ürün")}</button>}
        </div>
      </div>

      <div className="zen-panel p-4 flex flex-wrap gap-4 items-end">
        <div className="text-sm font-bold text-slate-600 dark:text-slate-300 mr-2">{tr("Güncel Kurlar:")}</div>
        {readOnly ? (
          <>
            <div className="zen-chip bg-slate-500/10 text-slate-600 dark:text-slate-300">EUR/TL: <b className="ml-1">{rates.eurToTl}</b></div>
            <div className="zen-chip bg-slate-500/10 text-slate-600 dark:text-slate-300">USD/TL: <b className="ml-1">{rates.usdToTl}</b></div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-[10px] font-semibold mb-1 text-slate-500">EUR / TL</label>
              <input type="number" step="0.01" className="zen-input !w-28" value={rates.eurToTl} onChange={(e) => setRates({ ...rates, eurToTl: Number(e.target.value) })} data-testid="rate-eur" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold mb-1 text-slate-500">{tr("USD / TL (+%2 uygulanır)")}</label>
              <input type="number" step="0.01" className="zen-input !w-28" value={rates.usdToTl} onChange={(e) => setRates({ ...rates, usdToTl: Number(e.target.value) })} data-testid="rate-usd" />
            </div>
            <button onClick={saveRates} className="zen-btn-primary" data-testid="save-rates">{tr("Kaydet")}</button>
            <button onClick={refreshTCMB} className="zen-btn-ghost flex items-center gap-1.5" data-testid="refresh-tcmb-btn" title="TCMB'den güncel kuru çek">
              <RefreshCw className="w-3.5 h-3.5" /> {tr("Kurları Şimdi Güncelle")}
            </button>
          </>
        )}
      </div>

      <div className="zen-panel overflow-hidden">
        <table className="zen-table">
          <thead><tr><th>{tr("Katalog")}</th><th>{tr("Kod")}</th><th>{tr("Kategori")}</th><th>{tr("Ürün")}</th><th>{tr("Birim")}</th><th className="text-right">{tr("Fiyat")}</th>{!readOnly && <th></th>}</tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid="pricing-row">
                <td><span className="zen-chip bg-[#E5322A]/10 text-[#E5322A]">{p.catalog}</span></td>
                <td className="zen-mono font-bold">{p.code}</td>
                <td className="text-xs text-slate-500">{p.category}</td>
                <td className="font-semibold">{p.name}</td>
                <td className="text-xs">{p.unit}</td>
                <td className="text-right zen-mono font-bold text-[#E5322A]">{CURRENCY_SYMBOL[p.currency]}{p.basePrice}</td>
                {!readOnly && (
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(p)} className="p-1.5 hover:bg-rose-500/10 text-rose-500 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={readOnly ? 6 : 7} className="text-center py-8 text-slate-500 text-sm">{tr("Sonuç bulunamadı")}</td></tr>}
          </tbody>
        </table>
      </div>
      {!readOnly && editing && <PriceForm data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
function PriceForm({ data, onClose, onSave }) {
  const { tr, lang } = useContext(UICtx);
  const [f, setF] = useState({ catalog: "Artbey", category: "Zip Perde", code: "", name: "", unit: "m²", basePrice: 0, currency: "EUR", ...data });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-lg p-6 zen-anim-up">
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold">{data?.id ? "Ürün Düzenle" : "Yeni Fiyat"}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div><label className="block text-xs font-semibold mb-1">{tr("Katalog")}</label><select className="zen-input" value={f.catalog} onChange={(e) => setF({ ...f, catalog: e.target.value })}><option>Artbey</option><option>Vatan</option><option>Akser</option><option>{tr("Diğer")}</option></select></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Kategori")}</label><input className="zen-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Kod")}</label><input className="zen-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Birim")}</label><select className="zen-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}><option>m²</option><option>{tr("adet")}</option><option>{tr("mtül")}</option></select></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Ürün Adı")}</label><input className="zen-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Birim Fiyat")}</label><input type="number" className="zen-input" value={f.basePrice} onChange={(e) => setF({ ...f, basePrice: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Para Birimi")}</label><select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}><option>EUR</option><option>USD</option><option>TL</option></select></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="zen-btn-ghost">{tr("İptal")}</button><button onClick={() => onSave(f)} className="zen-btn-primary">{tr("Kaydet")}</button></div>
      </div>
    </div>
  );
}

// ============ ACCOUNTING MODULE ============

// BalanceEditModal: Cari açılış bakiyesini ve net bakiyeyi düzenle
function BalanceEditModal({ customer: c, onClose, onSaved }) {
  const [openingBalance, setOpeningBalance] = useState(c.openingBalance || 0);
  const [recalculating, setRecalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveOpeningBalance = async () => {
    setSaving(true);
    try {
      // Açılış bakiyesini güncelle ve net bakiyeyi yeniden hesapla
      await api.put(`/customers/${c.id}`, { openingBalance: Number(openingBalance) });
      // Tüm hareketlerden net bakiyeyi yeniden hesapla
      const resp = await api.post(`/customers/${c.id}/recalculate-balance`);
      const fc = (await api.get("/customers")).data.find(x => x.id === c.id);
      toast.success(`Bakiye güncellendi: ${resp.data?.balance?.toFixed(2)}`);
      if (fc) onSaved(fc);
    } catch (e) {
      toast.error("Güncelleme hatası: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const resetBalance = async () => {
    if (!window.confirm("Bakiyeyi ve açılış bakiyesini sıfırlamak istediğinizden emin misiniz?")) return;
    setSaving(true);
    try {
      await api.put(`/customers/${c.id}`, { openingBalance: 0 });
      await api.post(`/customers/${c.id}/recalculate-balance`);
      const fc = (await api.get("/customers")).data.find(x => x.id === c.id);
      toast.success("Bakiye sıfırlandı");
      if (fc) onSaved(fc);
    } catch (e) {
      toast.error("Hata: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const recalcBalance = async () => {
    setRecalculating(true);
    try {
      const resp = await api.post(`/customers/${c.id}/recalculate-balance`);
      const fc = (await api.get("/customers")).data.find(x => x.id === c.id);
      toast.success(`Bakiye yeniden hesaplandı: ${resp.data?.balance?.toFixed(2)}`);
      if (fc) onSaved(fc);
    } catch (e) {
      toast.error("Hata: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-md p-6 zen-anim-up" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-bold flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-400" /> Bakiye Yönetimi</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="space-y-4">
          {/* Mevcut bakiye bilgisi */}
          <div className="p-3 rounded-lg bg-white/3 border border-white/8">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Mevcut Durum</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-slate-600">Net Bakiye</div>
                <div className={`text-sm font-black ${(c.balance || 0) > 0 ? "text-rose-400" : (c.balance || 0) < 0 ? "text-emerald-400" : "text-slate-400"}`}>
                  {(c.balance || 0).toFixed(2)} {c.currency}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-600">Açılış Bakiyesi</div>
                <div className="text-sm font-black text-slate-300">{(c.openingBalance || 0).toFixed(2)} {c.currency}</div>
              </div>
            </div>
          </div>

          {/* Açılış bakiyesini değiştir */}
          <div>
            <label className="block text-xs font-semibold mb-1.5 text-slate-300">
              Açılış Bakiyesi Düzelt <span className="text-slate-500 font-normal">(Hareketlerden önce mevcut bakiye)</span>
            </label>
            <div className="flex gap-2">
              <input
                data-testid="opening-balance-input"
                type="number"
                step="0.01"
                className="zen-input flex-1"
                value={openingBalance}
                onChange={e => setOpeningBalance(e.target.value)}
                placeholder="0.00"
              />
              <span className="flex items-center text-xs text-slate-500 px-2">{c.currency}</span>
            </div>
            <div className="text-[10px] text-slate-600 mt-1">
              Pozitif = müşteri bize borçlu | Negatif = biz müşteriye borçluyuz
            </div>
          </div>

          {/* Yeniden hesapla */}
          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/15">
            <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Net Bakiyeyi Yeniden Hesapla</div>
            <div className="text-[11px] text-slate-500 mb-2">Tüm hareketlerden (tahsilat/tediye/tahakkuk) bakiyeyi sıfırdan hesaplar.</div>
            <button
              data-testid="recalc-balance-btn"
              onClick={recalcBalance}
              disabled={recalculating}
              className="zen-btn-ghost !text-xs !py-1"
            >
              {recalculating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {recalculating ? "Hesaplanıyor..." : "Yeniden Hesapla"}
            </button>
          </div>
        </div>

        <div className="flex justify-between mt-5 gap-2">
          <button
            data-testid="balance-reset-btn"
            onClick={resetBalance}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 transition-colors"
          >
            <Trash2 className="w-3 h-3" /> Sıfırla
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="zen-btn-ghost !text-xs">İptal</button>
            <button
              data-testid="balance-save-btn"
              onClick={saveOpeningBalance}
              disabled={saving}
              className="zen-btn-primary !text-xs"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? "Kaydediliyor..." : "Kaydet & Hesapla"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CustomersModule, PricingModule };
export default CustomersModule;
